import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { hostsAPI, healthcheckAPI } from '../services/api';
import { classificationAPI } from '../services/api';
import { ExternalLink, Server, Cpu, Link as LinkIcon, Search, Filter, ArrowUpDown, ChevronDown, ChevronUp, Eye, EyeOff, Download, List, Grid, Shield, Upload, Trash2, Pencil } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { commentsAPI } from '../services/api';
import { useNotification } from '../hooks/useNotification';
import * as XLSX from 'xlsx';

const Hosts = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [displayedHosts, setDisplayedHosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [expandedHosts, setExpandedHosts] = useState(new Set());
  const [hoveredDomain, setHoveredDomain] = useState(null);
  
  // Filter and sort states
  const [urlFilter, setUrlFilter] = useState('');
  const [urlFilterDebounced, setUrlFilterDebounced] = useState('');
  const [techFilter, setTechFilter] = useState('');
  const [ipFilter, setIpFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [useRegex, setUseRegex] = useState(false);
  const [sortBy, setSortBy] = useState('url'); // 'url', 'tech', 'links', 'ip'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc', 'desc'
  const [statusFilter, setStatusFilter] = useState(''); // '', 'active', 'stale', 'gone', 'manual'
  
  // New view mode and link filtering states
  const [viewMode, setViewMode] = useState('hosts'); // 'hosts' or 'all-links'
  const [linkFilters, setLinkFilters] = useState({
    statusCode: '',
    sizeMin: '',
    sizeMax: '',
    tech: '',
    urlSubstring: '',
    ip: ''
  });
  const [expandedLinks, setExpandedLinks] = useState(new Set());
  
  // All Links sorting states
  const [allLinksSortBy, setAllLinksSortBy] = useState('url'); // 'url', 'host', 'status', 'size', 'tech', 'ip'
  const [allLinksSortOrder, setAllLinksSortOrder] = useState('asc'); // 'asc', 'desc'
  
  // Individual host links filtering and sorting states
  const [hostLinksSettings, setHostLinksSettings] = useState({}); // { hostId: { filters: {}, sortBy: '', sortOrder: '' } }
  
  // Pagination state for locations within expanded hosts
  const [hostLocationsPages, setHostLocationsPages] = useState({}); // { hostId: pageNumber }
  const LOCATIONS_PER_PAGE = 100;
  
  // Pagination for All Links View
  const [allLinksPage, setAllLinksPage] = useState(1);
  const [allLinksLimit] = useState(20);
  const [allLinks, setAllLinks] = useState([]);
  const [allLinksTotal, setAllLinksTotal] = useState(0);
  const [allLinksLoading, setAllLinksLoading] = useState(false);
  
  // Available tags and technologies for dropdowns
  const [availableTags, setAvailableTags] = useState([]);
  const [availableTechnologies, setAvailableTechnologies] = useState([]);
  
  const limit = 20;
  const { showSuccess, showError } = useNotification();
  const [hostComments, setHostComments] = useState({}); // hostId -> { items, page, total }
  const [hostNewComment, setHostNewComment] = useState({}); // hostId -> text
  const [hostEditingId, setHostEditingId] = useState({}); // hostId -> commentId | null
  const [hostEditingBody, setHostEditingBody] = useState({}); // hostId -> text
  
  // Tag management states
  const [hostNewTag, setHostNewTag] = useState({}); // hostId -> tag text
  const [showBulkTagModal, setShowBulkTagModal] = useState(false);
  const [bulkTagInput, setBulkTagInput] = useState('');
  const [bulkTagPreview, setBulkTagPreview] = useState([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFileName, setImportFileName] = useState('');
  const [importHostsPayload, setImportHostsPayload] = useState([]);
  const [importSummary, setImportSummary] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [showRegexDeleteModal, setShowRegexDeleteModal] = useState(false);
  const [deleteRegexPattern, setDeleteRegexPattern] = useState('');
  const [deleteRegexFlags, setDeleteRegexFlags] = useState('i');
  const [deleteRegexSummary, setDeleteRegexSummary] = useState(null);
  const [deleteRegexLoading, setDeleteRegexLoading] = useState(false);
  const [showEditHostModal, setShowEditHostModal] = useState(false);
  const [editingHostRef, setEditingHostRef] = useState(null);
  const [editingHostJson, setEditingHostJson] = useState('');
  const [editHostLoading, setEditHostLoading] = useState(false);
  
  // Comments collapsible state
  const [collapsedCommentsSections, setCollapsedCommentsSections] = useState(new Set());
  // Classification collapsible state and cache
  const [collapsedClassificationSections, setCollapsedClassificationSections] = useState(new Set());
  const [hostClassification, setHostClassification] = useState({}); // hostId -> classification doc or null
  // Healthcheck history state
  const [collapsedHealthcheckSections, setCollapsedHealthcheckSections] = useState(new Set());
  const [hostHealthcheckHistory, setHostHealthcheckHistory] = useState({}); // hostId -> history array

  // Column resizing state
  const [hostsColumnWidths, setHostsColumnWidths] = useState(() => {
    const saved = localStorage.getItem('hostsColumnWidths');
    return saved ? JSON.parse(saved) : {
      url: 300,
      tags: 200,
      tech: 150,
      links: 100,
      vulnerabilities: 120,
      comments: 100,
      ip: 150,
      actions: 100
    };
  });
  const [linksColumnWidths, setLinksColumnWidths] = useState(() => {
    const saved = localStorage.getItem('linksColumnWidths');
    return saved ? JSON.parse(saved) : {
      url: 300,
      host: 200,
      status: 100,
      size: 100,
      tech: 150,
      ip: 150,
      actions: 100
    };
  });
  const [resizingColumn, setResizingColumn] = useState(null); // { table: 'hosts'|'links', index: number }

  const toggleClassificationSection = (hostId, hostName) => {
    const newCollapsed = new Set(collapsedClassificationSections);
    if (newCollapsed.has(hostId)) {
      newCollapsed.delete(hostId);
    } else {
      newCollapsed.add(hostId);
    }
    setCollapsedClassificationSections(newCollapsed);
  };

  const loadHostClassification = async (hostId, hostName) => {
    try {
      const res = await classificationAPI.getLatestByHost(hostName);
      setHostClassification(prev => ({ ...prev, [hostId]: res.data?.result || null }));
    } catch (e) {
      setHostClassification(prev => ({ ...prev, [hostId]: null }));
    }
  };

  const toggleHealthcheckSection = (hostId, hostName) => {
    const next = new Set(collapsedHealthcheckSections);
    if (next.has(hostId)) {
      next.delete(hostId);
    } else {
      next.add(hostId);
      if (!hostHealthcheckHistory[hostId]) {
        loadHostHealthcheckHistory(hostId, hostName);
      }
    }
    setCollapsedHealthcheckSections(next);
  };

  const loadHostHealthcheckHistory = async (hostId, hostName) => {
    try {
      const res = await healthcheckAPI.getHostHistory(hostName);
      setHostHealthcheckHistory(prev => ({ ...prev, [hostId]: res.data?.history || [] }));
    } catch (e) {
      setHostHealthcheckHistory(prev => ({ ...prev, [hostId]: [] }));
    }
  };

  // Handle URL parameters for filtering
  useEffect(() => {
    const filterParam = searchParams.get('filter');
    if (filterParam) {
      setUrlFilter(filterParam);
      // Auto-enable regex if the filter looks like a regex pattern
      if (filterParam.startsWith('^') || filterParam.endsWith('$') || 
          filterParam.includes('\\') || filterParam.includes('[') || 
          filterParam.includes('(') || filterParam.includes('|')) {
        setUseRegex(true);
      }
    }
  }, [searchParams]);

  // Debounce urlFilter for API calls (2 seconds delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setUrlFilterDebounced(urlFilter);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [urlFilter]);

  // Reset to page 1 when filters or sorting change (but not on initial load)
  useEffect(() => {
    setPage(1);
  }, [urlFilterDebounced, techFilter, ipFilter, tagFilter, useRegex, sortBy, sortOrder, statusFilter]);

  // Fetch hosts with filters, sorting, and pagination
  useEffect(() => {
    fetchFilteredHosts();
  }, [page, urlFilterDebounced, techFilter, ipFilter, tagFilter, useRegex, sortBy, sortOrder, statusFilter]);

  // Save column widths to localStorage
  useEffect(() => {
    localStorage.setItem('hostsColumnWidths', JSON.stringify(hostsColumnWidths));
  }, [hostsColumnWidths]);

  useEffect(() => {
    localStorage.setItem('linksColumnWidths', JSON.stringify(linksColumnWidths));
  }, [linksColumnWidths]);

  // Reset All Links page when filters or sorting change
  useEffect(() => {
    setAllLinksPage(1);
  }, [linkFilters, allLinksSortBy, allLinksSortOrder, useRegex]);

  const fetchAvailableData = async () => {
    try {
      const [tagsRes, techRes] = await Promise.all([
        hostsAPI.getTags(),
        hostsAPI.getTechnologies()
      ]);
      setAvailableTags(tagsRes.data?.tags || []);
      setAvailableTechnologies(techRes.data?.technologies || []);
    } catch (err) {
      console.error('Не удалось получить теги/технологии:', err);
    }
  };

  // Fetch available tags and technologies on mount
  useEffect(() => {
    fetchAvailableData();
  }, []);

  // Fetch all links when in all-links view mode
  useEffect(() => {
    if (viewMode === 'all-links') {
      fetchAllLinks();
    }
  }, [viewMode, allLinksPage, linkFilters, allLinksSortBy, allLinksSortOrder, useRegex]);

  const fetchAllLinks = async () => {
    try {
      setAllLinksLoading(true);
      const filters = {
        status_code: linkFilters.statusCode,
        size_min: linkFilters.sizeMin,
        size_max: linkFilters.sizeMax,
        tech: linkFilters.tech,
        url: linkFilters.urlSubstring,
        ip: linkFilters.ip,
        use_regex: useRegex
      };
      const response = await hostsAPI.getAllLinks(
        allLinksPage,
        allLinksLimit,
        filters,
        allLinksSortBy,
        allLinksSortOrder
      );
      setAllLinks(response.data.links || []);
      setAllLinksTotal(response.data.total || 0);
    } catch (err) {
      setError(err.response?.data?.message || 'Не удалось получить ссылки');
    } finally {
      setAllLinksLoading(false);
    }
  };

  const fetchFilteredHosts = async () => {
    try {
      setLoading(true);
      const filters = {
        url: urlFilterDebounced,
        tech: techFilter,
        ip: ipFilter,
        tag: tagFilter,
        status: statusFilter,
        use_regex: useRegex
      };
      const response = await hostsAPI.getHosts(page, limit, filters, sortBy, sortOrder);
      setDisplayedHosts(response.data.hosts);
      setTotal(response.data.total);
    } catch (err) {
      setError(err.response?.data?.message || 'Не удалось получить хосты');
    } finally {
      setLoading(false);
    }
  };

  const formatTechnologies = (techArray) => {
    if (!techArray || !Array.isArray(techArray) || techArray.length === 0) return '0';
    if (techArray.length <= 2) return techArray.join(', ');
    return `${techArray.slice(0, 2).join(', ')} и ещё ${techArray.length - 2}`;
  };

  const toggleHostExpansion = (hostId) => {
    const newExpanded = new Set(expandedHosts);
    if (newExpanded.has(hostId)) {
      newExpanded.delete(hostId);
    } else {
      newExpanded.add(hostId);
      // Reset locations page to 1 when expanding
      setHostLocationsPages(prev => ({ ...prev, [hostId]: 1 }));
      // lazy-load comments on expand
      if (!hostComments[hostId]) {
        loadHostComments(hostId, 1);
      }
      // lazy-load classification on expand
      const host = displayedHosts.find(h => h._id === hostId);
      if (host && !hostClassification[hostId]) {
        loadHostClassification(hostId, host.host);
      }
    }
    setExpandedHosts(newExpanded);
  };

  const loadHostComments = async (hostId, p = 1) => {
    try {
      const res = await commentsAPI.list({ scope: 'host', hostId: hostId, page: p, limit: 10 });
      setHostComments(prev => ({
        ...prev,
        [hostId]: { items: res.data.comments || [], page: res.data.page || p, total: res.data.total || 0 }
      }));
    } catch (e) {
      // ignore
    }
  };

  const toggleCommentsSection = (hostId) => {
    const newCollapsed = new Set(collapsedCommentsSections);
    if (newCollapsed.has(hostId)) {
      newCollapsed.delete(hostId);
    } else {
      newCollapsed.add(hostId);
    }
    setCollapsedCommentsSections(newCollapsed);
  };

  const postHostComment = async (hostId) => {
    const text = (hostNewComment[hostId] || '').trim();
    if (!text) return;
    try {
      await commentsAPI.create({ scope: 'host', host_id: hostId, body_md: text });
      setHostNewComment(prev => ({ ...prev, [hostId]: '' }));
      loadHostComments(hostId, 1);
      showSuccess('Комментарий добавлен');
    } catch (e) {
      showError(e.response?.data?.message || 'Не удалось добавить комментарий');
    }
  };

  const startEditHostComment = (hostId, comment) => {
    setHostEditingId(prev => ({ ...prev, [hostId]: comment._id }));
    setHostEditingBody(prev => ({ ...prev, [hostId]: comment.body_md || '' }));
  };

  const cancelEditHostComment = (hostId) => {
    setHostEditingId(prev => ({ ...prev, [hostId]: null }));
    setHostEditingBody(prev => ({ ...prev, [hostId]: '' }));
  };

  const saveHostComment = async (hostId) => {
    const commentId = hostEditingId[hostId];
    const body = (hostEditingBody[hostId] || '').trim();
    if (!commentId || !body) return;
    try {
      await commentsAPI.update(commentId, { body_md: body });
      cancelEditHostComment(hostId);
      loadHostComments(hostId, hostComments[hostId]?.page || 1);
      showSuccess('Комментарий обновлён');
    } catch (e) {
      showError(e.response?.data?.message || 'Не удалось обновить комментарий');
    }
  };

  const deleteHostComment = async (hostId, commentId) => {
    if (!window.confirm('Удалить этот комментарий?')) return;
    try {
      await commentsAPI.remove(commentId);
      loadHostComments(hostId, hostComments[hostId]?.page || 1);
      showSuccess('Комментарий удалён');
    } catch (e) {
      showError(e.response?.data?.message || 'Не удалось удалить комментарий');
    }
  };

  // Tag management functions
  const addTagToHost = async (hostId) => {
    const tagText = (hostNewTag[hostId] || '').trim();
    if (!tagText) return;
    
    try {
      await hostsAPI.addTags(hostId, [tagText]);
      setHostNewTag(prev => ({ ...prev, [hostId]: '' }));
      await fetchFilteredHosts();
      await fetchAvailableData();
      showSuccess('Тег успешно добавлен');
    } catch (e) {
      showError(e.response?.data?.message || 'Не удалось добавить тег');
    }
  };

  const removeTagFromHost = async (hostId, tag) => {
    try {
      await hostsAPI.removeTags(hostId, [tag]);
      await fetchFilteredHosts();
      await fetchAvailableData();
      showSuccess('Тег успешно удалён');
    } catch (e) {
      showError(e.response?.data?.message || 'Не удалось удалить тег');
    }
  };

  const openBulkTagModal = () => {
    const sourceHosts = viewMode === 'all-links'
      ? allLinks.map(link => link.hostUrl).filter(Boolean)
      : displayedHosts.map(host => host.host).filter(Boolean);
    const uniqueHosts = [...new Set(sourceHosts)];
    setBulkTagPreview(uniqueHosts);
    setBulkTagInput('');
    setShowBulkTagModal(true);
  };

  const closeBulkTagModal = () => {
    setShowBulkTagModal(false);
    setBulkTagInput('');
    setBulkTagPreview([]);
  };

  const applyBulkTags = async () => {
    const tags = bulkTagInput.split(',').map(t => t.trim()).filter(Boolean);
    if (tags.length === 0) {
      showError('Введите хотя бы один тег');
      return;
    }
    
    if (bulkTagPreview.length === 0) {
      showError('Нет хостов для добавления тегов');
      return;
    }

    try {
      const response = await hostsAPI.bulkAddTags(bulkTagPreview, tags);
      showSuccess(response.data.message || `Теги добавлены для ${response.data.modified_count} хостов`);
      closeBulkTagModal();
      await fetchFilteredHosts();
      await fetchAvailableData();
    } catch (e) {
      showError(e.response?.data?.message || 'Не удалось добавить теги');
    }
  };

  const refreshHostsData = async () => {
    await fetchFilteredHosts();
    if (viewMode === 'all-links') {
      await fetchAllLinks();
    }
    await fetchAvailableData();
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    setImportFileName('');
    setImportHostsPayload([]);
    setImportSummary(null);
    setImportLoading(false);
  };

  const handleImportFileSelect = async (event) => {
    const inputElement = event.target;
    const file = inputElement.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.json')) {
      showError('Выберите JSON-файл');
      return;
    }

    try {
      setImportLoading(true);
      const text = await file.text();
      const parsed = JSON.parse(text);
      const hosts = Array.isArray(parsed)
        ? parsed
        : Array.isArray(parsed?.hosts)
          ? parsed.hosts
          : [];

      if (!Array.isArray(hosts) || hosts.length === 0) {
        showError('JSON должен содержать непустой массив хостов');
        return;
      }

      setImportFileName(file.name);
      setImportHostsPayload(hosts);

      const dryRunResponse = await hostsAPI.importHosts(hosts, true);
      setImportSummary(dryRunResponse.data || null);
      showSuccess('Файл импорта разобран. Пробный прогон выполнен.');
    } catch (e) {
      if (e instanceof SyntaxError) {
        showError('Невалидный формат JSON');
      } else {
        showError(e.response?.data?.message || 'Не удалось разобрать файл импорта');
      }
    } finally {
      setImportLoading(false);
      if (inputElement) {
        inputElement.value = '';
      }
    }
  };

  const applyImportHosts = async () => {
    if (!importHostsPayload || importHostsPayload.length === 0) {
      showError('Нет загруженных хостов для импорта');
      return;
    }
    try {
      setImportLoading(true);
      const response = await hostsAPI.importHosts(importHostsPayload, false);
      setImportSummary(response.data || null);
      await refreshHostsData();
      showSuccess(response.data?.message || 'Хосты успешно импортированы');
      closeImportModal();
    } catch (e) {
      showError(e.response?.data?.message || 'Не удалось импортировать хосты');
    } finally {
      setImportLoading(false);
    }
  };

  const closeRegexDeleteModal = () => {
    setShowRegexDeleteModal(false);
    setDeleteRegexPattern('');
    setDeleteRegexFlags('i');
    setDeleteRegexSummary(null);
    setDeleteRegexLoading(false);
  };

  const runRegexDelete = async (dryRun = true) => {
    const pattern = deleteRegexPattern.trim();
    const flags = deleteRegexFlags.trim();

    if (!pattern) {
      showError('Нужно указать шаблон регулярного выражения');
      return;
    }

    try {
      setDeleteRegexLoading(true);
      const response = await hostsAPI.deleteHostsByRegex(pattern, dryRun, flags);
      setDeleteRegexSummary(response.data || null);
      if (dryRun) {
        showSuccess(`Пробный прогон: найдено ${response.data?.matched_count || 0} хостов`);
      } else {
        await refreshHostsData();
        showSuccess(response.data?.message || 'Хосты удалены');
        closeRegexDeleteModal();
      }
    } catch (e) {
      showError(e.response?.data?.message || 'Не удалось выполнить удаление по регулярному выражению');
    } finally {
      setDeleteRegexLoading(false);
    }
  };

  const openEditHostModal = (host) => {
    if (!host || !host._id) return;
    const editable = {
      host: host.host || '',
      tech: host.tech || [],
      tags: host.tags || [],
      modify: host.modify || {},
      locations: getHostLocations(host) || [],
      relations: host.relations || { domains: [], links: [] }
    };
    setEditingHostRef({ _id: host._id, host: host.host || '' });
    setEditingHostJson(JSON.stringify(editable, null, 2));
    setShowEditHostModal(true);
  };

  const closeEditHostModal = () => {
    setShowEditHostModal(false);
    setEditingHostRef(null);
    setEditingHostJson('');
    setEditHostLoading(false);
  };

  const saveEditedHost = async () => {
    if (!editingHostRef?._id) return;
    try {
      setEditHostLoading(true);
      const payload = JSON.parse(editingHostJson);
      await hostsAPI.updateHost(editingHostRef._id, payload);
      await refreshHostsData();
      showSuccess('Хост успешно обновлён');
      closeEditHostModal();
    } catch (e) {
      if (e instanceof SyntaxError) {
        showError('Невалидный JSON в редакторе хоста');
      } else {
        showError(e.response?.data?.message || 'Не удалось обновить хост');
      }
    } finally {
      setEditHostLoading(false);
    }
  };

  const deleteHostById = async (host) => {
    if (!host?._id) return;
    if (!window.confirm(`Удалить хост "${host.host}"? Также будут удалены связанные комментарии, классификация и уязвимости.`)) {
      return;
    }

    try {
      await hostsAPI.deleteHost(host._id);
      await refreshHostsData();
      setExpandedHosts(prev => {
        const next = new Set(prev);
        next.delete(host._id);
        return next;
      });
      showSuccess('Хост успешно удалён');
    } catch (e) {
      showError(e.response?.data?.message || 'Не удалось удалить хост');
    }
  };

  // Helper function to get tag color
  const getTagColor = (index) => {
    const colors = [
      { bg: '#dbeafe', text: '#1e40af', border: '#93c5fd' },
      { bg: '#dcfce7', text: '#166534', border: '#86efac' },
      { bg: '#fef3c7', text: '#92400e', border: '#fcd34d' },
      { bg: '#fce7f3', text: '#9f1239', border: '#fbcfe8' },
      { bg: '#f3e8ff', text: '#6b21a8', border: '#e9d5ff' },
      { bg: '#e0f2fe', text: '#075985', border: '#7dd3fc' },
    ];
    return colors[index % colors.length];
  };

  const toggleLinkExpansion = (linkId) => {
    const newExpanded = new Set(expandedLinks);
    if (newExpanded.has(linkId)) {
      newExpanded.delete(linkId);
    } else {
      newExpanded.add(linkId);
    }
    setExpandedLinks(newExpanded);
  };

  // Get all links from displayed hosts for the "all-links" view
  const getAllLinks = () => {
    const allLinks = [];
    if (!displayedHosts || !Array.isArray(displayedHosts)) return allLinks;
    
    displayedHosts.forEach(host => {
      if (!host || !host._id) return;
      
      const locations = getHostLocations(host);
      if (locations && locations.length > 0) {
        locations.forEach((location, index) => {
          if (!location) return;
          
          const linkObj = typeof location === 'string' ? { url: location } : location;
          allLinks.push({
            ...linkObj,
            hostUrl: host.host || '',
            id: `${host._id}-${index}`
          });
        });
      }
    });
    return allLinks;
  };

  // Filter and sort links based on current filters
  const getFilteredLinks = () => {
    let links = getAllLinks();
    
    if (linkFilters.statusCode) {
      links = links.filter(link => 
        link.status_code && link.status_code.toString().includes(linkFilters.statusCode)
      );
    }
    
    if (linkFilters.sizeMin) {
      links = links.filter(link => 
        link.size && link.size >= parseInt(linkFilters.sizeMin)
      );
    }
    
    if (linkFilters.sizeMax) {
      links = links.filter(link => 
        link.size && link.size <= parseInt(linkFilters.sizeMax)
      );
    }
    
    if (linkFilters.tech) {
      links = links.filter(link => 
        link.tech && link.tech.some(tech => 
          matchText(tech, linkFilters.tech)
        )
      );
    }
    
    if (linkFilters.urlSubstring) {
      links = links.filter(link => 
        matchText(link.url, linkFilters.urlSubstring)
      );
    }
    
    if (linkFilters.ip) {
      links = links.filter(link => 
        link.ip && matchText(link.ip, linkFilters.ip)
      );
    }
    
    return links;
  };

  // Get sorted and filtered links (now from server)
  const getSortedAndFilteredLinks = () => {
    // Return links from server - they are already sorted and filtered
    return allLinks;
  };

  // Get settings for a specific host
  const getHostSettings = (hostId) => {
    return hostLinksSettings[hostId] || {
      filters: {
        statusCode: '',
        sizeMin: '',
        sizeMax: '',
        tech: '',
        urlSubstring: ''
      },
      sortBy: 'url',
      sortOrder: 'asc'
    };
  };

  // Update settings for a specific host
  const updateHostSettings = (hostId, newSettings) => {
    setHostLinksSettings(prev => ({
      ...prev,
      [hostId]: {
        ...getHostSettings(hostId),
        ...newSettings
      }
    }));
    // Reset locations page to 1 when filters or sorting change
    setHostLocationsPages(prev => ({ ...prev, [hostId]: 1 }));
  };

  // Filter and sort links for a specific host
  const getFilteredAndSortedHostLinks = (host) => {
    if (!host || !host._id) return [];
    
    const locations = getHostLocations(host);
    if (!locations || locations.length === 0) return [];
    
    const settings = getHostSettings(host._id);
    const { filters, sortBy, sortOrder } = settings;
    
    let links = locations.map((location, index) => {
      if (!location) return null;
      
      const linkObj = typeof location === 'string' ? { url: location } : location;
      return {
        ...linkObj,
        id: `${host._id}-${index}`
      };
    }).filter(link => link !== null);
    
    // Apply filters
    if (filters.statusCode) {
      links = links.filter(link => 
        link.status_code && link.status_code.toString().includes(filters.statusCode)
      );
    }
    
    if (filters.sizeMin) {
      links = links.filter(link => 
        link.size && link.size >= parseInt(filters.sizeMin)
      );
    }
    
    if (filters.sizeMax) {
      links = links.filter(link => 
        link.size && link.size <= parseInt(filters.sizeMax)
      );
    }
    
    if (filters.tech) {
      links = links.filter(link => 
        link.tech && link.tech.some(tech => 
          matchText(tech, filters.tech)
        )
      );
    }
    
    if (filters.urlSubstring) {
      links = links.filter(link => 
        matchText(link.url, filters.urlSubstring)
      );
    }
    
    // Apply sorting
    links.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'url':
          aValue = (a.host || '').toLowerCase();
          bValue = (b.host || '').toLowerCase();
          break;
        case 'status':
          aValue = a.status_code || 0;
          bValue = b.status_code || 0;
          break;
        case 'size':
          aValue = a.size || 0;
          bValue = b.size || 0;
          break;
        case 'tech':
          aValue = a.tech?.length || 0;
          bValue = b.tech?.length || 0;
          break;
        default:
          aValue = (a.host || '').toLowerCase();
          bValue = (b.host || '').toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
    
    return links;
  };

  // Get paginated locations for a specific host
  const getPaginatedHostLinks = (host) => {
    const allLinks = getFilteredAndSortedHostLinks(host);
    const currentPage = hostLocationsPages[host._id] || 1;
    const startIndex = (currentPage - 1) * LOCATIONS_PER_PAGE;
    const endIndex = startIndex + LOCATIONS_PER_PAGE;
    return {
      links: allLinks.slice(startIndex, endIndex),
      total: allLinks.length,
      currentPage,
      totalPages: Math.ceil(allLinks.length / LOCATIONS_PER_PAGE),
      startIndex: startIndex + 1,
      endIndex: Math.min(endIndex, allLinks.length)
    };
  };

  const getStatusColor = (statusCode) => {
    if (!statusCode) return '#6b7280';
    if (statusCode >= 200 && statusCode < 300) return '#10b981';
    if (statusCode >= 300 && statusCode < 400) return '#3b82f6';
    if (statusCode >= 400 && statusCode < 500) return '#f59e0b';
    if (statusCode >= 500) return '#ef4444';
    return '#6b7280';
  };

  // Helper function to get first IP from host locations
  const getFirstIpFromHost = (host) => {
    if (!host) return null;
    const locations = host.locations || host.links || [];
    for (const location of locations) {
      if (typeof location === 'object' && location && location.ip) {
        return location.ip;
      }
    }
    return null;
  };

  // Helper function to get all locations (backward compatible)
  const getHostLocations = (host) => {
    if (!host) return [];
    return host.locations || host.links || [];
  };

  // Helper function to get relations (new object structure)
  const getHostRelations = (host) => {
    if (!host) return [];
    const relations = host.relations || {};
    return relations.links || [];
  };

  // Helper function to get domains from relations
  const getHostDomains = (host) => {
    if (!host) return [];
    const relations = host.relations || {};
    return relations.domains || [];
  };

  // Helper function to determine protocol (http/https) for a host
  const getHostProtocol = (host) => {
    if (!host) return 'https'; // default to https
    
    const locations = host.locations || host.links || [];
    
    // Check locations for URLs with protocol
    for (const location of locations) {
      if (typeof location === 'string') {
        if (location.startsWith('https://')) return 'https';
        if (location.startsWith('http://')) return 'http';
      } else if (typeof location === 'object' && location && location.url) {
        const url = location.url;
        if (url.startsWith('https://')) return 'https';
        if (url.startsWith('http://')) return 'http';
      }
    }
    
    // Default to https if no protocol found
    return 'https';
  };

  // Function to navigate to host by domain name
  const navigateToHostByDomain = (domain) => {
    if (!domain) return;
    // Set filter to find host with this domain
    setUrlFilter(domain);
    // Scroll to top of table to see filtered results
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Helper function to match text (with regex support)
  const matchText = (text, filter) => {
    if (!filter) return true;
    if (!text) return false;
    
    if (useRegex) {
      try {
        const regex = new RegExp(filter, 'i');
        return regex.test(text);
      } catch (e) {
        // If regex is invalid, fall back to normal search
        return (text || '').toLowerCase().includes((filter || '').toLowerCase());
      }
    } else {
      return text.toLowerCase().includes(filter.toLowerCase());
    }
  };

  // Helper function to get vulnerability badge color based on highest severity
  const getVulnerabilityBadgeColor = (stats) => {
    if (!stats || !stats.total || stats.total === 0) return '#6b7280'; // Gray for no vulnerabilities
    if (stats.critical > 0) return '#8b0000'; // Dark red/Maroon for critical
    if (stats.high > 0) return '#ea580c'; // Red for high
    if (stats.medium > 0) return '#f59e0b'; // Yellow for medium
    return '#10b981'; // Green for low/info only
  };

  // Helper function to navigate to vulnerabilities page with filters
  const navigateToVulnerabilities = (host, severity = null) => {
    const hostRegex = `^${host.replace(/\./g, '\\.')}$`;
    const params = new URLSearchParams();
    params.set('host', hostRegex);
    if (severity) {
      params.set('severity', severity);
    }
    navigate(`/vulnerabilities?${params.toString()}`);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const handleHostLinksSort = (hostId, field) => {
    const settings = getHostSettings(hostId);
    const { sortBy, sortOrder } = settings;
    
    if (sortBy === field) {
      updateHostSettings(hostId, {
        sortOrder: sortOrder === 'asc' ? 'desc' : 'asc'
      });
    } else {
      updateHostSettings(hostId, {
        sortBy: field,
        sortOrder: 'asc'
      });
    }
  };

  const handleAllLinksSort = (field) => {
    if (allLinksSortBy === field) {
      setAllLinksSortOrder(allLinksSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setAllLinksSortBy(field);
      setAllLinksSortOrder('asc');
    }
  };

  // Column resizing handlers
  const handleResizeStart = (table, columnKey, e) => {
    e.preventDefault();
    setResizingColumn({ table, columnKey });
    const startX = e.clientX;
    const startWidth = table === 'hosts' ? hostsColumnWidths[columnKey] : linksColumnWidths[columnKey];

    const handleMouseMove = (e) => {
      const diff = e.clientX - startX;
      const newWidth = Math.max(50, startWidth + diff); // Minimum width 50px
      if (table === 'hosts') {
        setHostsColumnWidths(prev => ({ ...prev, [columnKey]: newWidth }));
      } else {
        setLinksColumnWidths(prev => ({ ...prev, [columnKey]: newWidth }));
      }
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      setResizingColumn(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Get paginated links for All Links View (now from server)
  const getPaginatedLinks = () => {
    // Links are already paginated on the server
    return allLinks;
  };

  // Download functions
  const downloadAsJSON = (data, filename) => {
    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadAsTXT = (data, filename) => {
    if (data.length === 0) return;
    
    const txtContent = data.map(host => host.host).join('\n');

    const blob = new Blob([txtContent], { type: 'text/plain;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadLinksAsTXT = (data, filename) => {
    if (data.length === 0) return;
    
    const allLinks = [];
    data.forEach(host => {
      const locations = getHostLocations(host);
      if (locations && locations.length > 0) {
        allLinks.push(...locations.map(loc => typeof loc === 'string' ? loc : loc.url));
      }
    });
    
    const txtContent = allLinks.join('\n');

    const blob = new Blob([txtContent], { type: 'text/plain;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadAsXLS = (data, filename) => {
    if (data.length === 0) return;

    const worksheetData = data.map(host => {
      // Helper function to truncate text to Excel's limit
      const truncateText = (text) => {
        if (!text) return '';
        const str = String(text);
        return str.length > 32700 ? str.substring(0, 32700) + '...' : str;
      };
      
      const locations = getHostLocations(host);
      const relations = getHostRelations(host);
      const domains = getHostDomains(host);
      
      return {
        'URL': truncateText(host.host),
        'Technologies': truncateText(host.tech?.join('; ') || ''),
        'Locations Count': locations.length,
        'Relations Count': relations.length,
        'Domains Count': domains.length,
        'Первый IP': truncateText(getFirstIpFromHost(host) || ''),
        'Locations': truncateText(locations.map(loc => typeof loc === 'string' ? loc : loc.url).join('; ') || ''),
        'Relations': truncateText(relations.map(rel => typeof rel === 'string' ? rel : rel.url).join('; ') || ''),
        'Domains': truncateText(domains.join('; ') || '')
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Хосты');
    
    XLSX.writeFile(workbook, filename);
  };

  const downloadData = async (format, dataType) => {
    try {
      let data, filename;
      const largeLimit = 1000000; // Large limit to get all data
      
      if (dataType === 'all') {
        // Fetch all hosts without filters
        const response = await hostsAPI.getHosts(1, largeLimit, {}, sortBy, sortOrder);
        data = response.data.hosts || [];
        filename = `all_hosts_${new Date().toISOString().split('T')[0]}`;
      } else if (dataType === 'filtered') {
        // Fetch all filtered hosts
        const filters = {
          url: urlFilterDebounced,
          tech: techFilter,
          ip: ipFilter,
          tag: tagFilter,
          use_regex: useRegex
        };
        const response = await hostsAPI.getHosts(1, largeLimit, filters, sortBy, sortOrder);
        data = response.data.hosts || [];
        filename = `filtered_hosts_${new Date().toISOString().split('T')[0]}`;
      } else if (dataType === 'urls') {
        // Fetch all hosts (filtered or all) and extract URLs
        const filters = {
          url: urlFilterDebounced,
          tech: techFilter,
          ip: ipFilter,
          tag: tagFilter,
          use_regex: useRegex
        };
        const response = await hostsAPI.getHosts(1, largeLimit, filters, sortBy, sortOrder);
        data = response.data.hosts || [];
        filename = `host_urls_${new Date().toISOString().split('T')[0]}`;
      } else if (dataType === 'links') {
        // Fetch all links from all hosts (filtered or all)
        const filters = {
          url: urlFilterDebounced,
          tech: techFilter,
          ip: ipFilter,
          tag: tagFilter,
          use_regex: useRegex
        };
        // First get all hosts
        const hostsResponse = await hostsAPI.getHosts(1, largeLimit, filters, sortBy, sortOrder);
        const allHosts = hostsResponse.data.hosts || [];
        // Extract all links from all hosts
        const allLinks = [];
        allHosts.forEach(host => {
          const locations = getHostLocations(host);
          if (locations && locations.length > 0) {
            allLinks.push(...locations.map(loc => typeof loc === 'string' ? loc : loc.url));
          }
        });
        data = allLinks;
        filename = `all_links_${new Date().toISOString().split('T')[0]}`;
      }

      if (!data || data.length === 0) {
        alert('Нет данных для скачивания');
        return;
      }

      switch (format) {
        case 'json':
          if (dataType === 'links') {
            // For links, save as array of URLs
            downloadAsJSON(data.map(url => ({ url })), `${filename}.json`);
          } else {
            downloadAsJSON(data, `${filename}.json`);
          }
          break;
        case 'xls':
          if (dataType === 'links') {
            // For links, convert to host-like structure for XLS
            const hostData = data.map(url => ({ host: url }));
            downloadAsXLS(hostData, `${filename}.xlsx`);
          } else {
            downloadAsXLS(data, `${filename}.xlsx`);
          }
          break;
        case 'txt':
          if (dataType === 'links') {
            // For links, save as plain text URLs
            const txtContent = Array.isArray(data) ? data.join('\n') : '';
            const blob = new Blob([txtContent], { type: 'text/plain;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${filename}.txt`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
          } else {
            downloadAsTXT(data, `${filename}.txt`);
          }
          break;
        default:
          break;
      }
    } catch (err) {
      console.error('Ошибка при скачивании данных:', err);
      alert('Не удалось скачать данные: ' + (err.response?.data?.message || err.message));
    }
  };

  // Download filtered links for a specific host
  const downloadHostLinks = (host) => {
    const filteredLinks = getFilteredAndSortedHostLinks(host);
    
    if (filteredLinks.length === 0) {
      alert('Для этого хоста нет отфильтрованных ссылок для скачивания');
      return;
    }

    const hostName = host.host.replace(/[^a-zA-Z0-9]/g, '_');
    const filename = `host_${hostName}_filtered_links_${new Date().toISOString().split('T')[0]}`;
    
    downloadAsJSON(filteredLinks, `${filename}.json`);
  };

  // Download functions for All Links View
  const downloadAllLinksData = async (format, dataType) => {
    try {
      let data, filename;
      const largeLimit = 1000000; // Large limit to get all data
      
      const filters = {
        status_code: linkFilters.statusCode,
        size_min: linkFilters.sizeMin,
        size_max: linkFilters.sizeMax,
        tech: linkFilters.tech,
        url: linkFilters.urlSubstring,
        ip: linkFilters.ip,
        use_regex: useRegex
      };
      
      if (dataType === 'all') {
        // Fetch all links without filters
        const response = await hostsAPI.getAllLinks(1, largeLimit, {}, allLinksSortBy, allLinksSortOrder);
        data = response.data.links || [];
        filename = `all_links_${new Date().toISOString().split('T')[0]}`;
      } else if (dataType === 'filtered') {
        // Fetch all filtered links
        const response = await hostsAPI.getAllLinks(1, largeLimit, filters, allLinksSortBy, allLinksSortOrder);
        data = response.data.links || [];
        filename = `filtered_links_${new Date().toISOString().split('T')[0]}`;
      }

      if (!data || data.length === 0) {
        alert('Нет данных для скачивания');
        return;
      }

      switch (format) {
        case 'json':
          downloadAsJSON(data, `${filename}.json`);
          break;
        case 'txt':
          const txtContent = data.map(link => link.url || (typeof link === 'string' ? link : '')).join('\n');
          const blob = new Blob([txtContent], { type: 'text/plain;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${filename}.txt`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          break;
        default:
          break;
      }
    } catch (err) {
      console.error('Ошибка при скачивании данных ссылок:', err);
      alert('Не удалось скачать данные: ' + (err.response?.data?.message || err.message));
    }
  };


  const totalPages = Math.ceil(total / limit);

  if (loading && displayedHosts.length === 0) {
    return (
      <div className="loading">
        <div>Загрузка хостов...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '28px' }}>
      <div style={{ marginBottom: '28px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
          <div className="page-header" style={{ marginBottom: 0 }}>
            <div className="page-header-icon">
              <Server size={24} />
            </div>
            <div className="page-header-text">
              <h1>Хосты</h1>
              <p>Всего: {total}, показано: {displayedHosts.length}</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              onClick={() => setShowImportModal(true)}
              className="btn btn-primary"
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <Upload size={14} />
              Подгрузка данных
            </button>
            <button
              onClick={() => setShowRegexDeleteModal(true)}
              className="btn btn-danger"
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <Trash2 size={14} />
              Удалить по regex
            </button>
          </div>
        </div>
      </div>

      {error && <div className="error">{error}</div>}

      {/* View Mode Toggle */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: 'var(--text-primary)',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            <Server size={20} />
            Режим просмотра
          </h3>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setViewMode('hosts')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                backgroundColor: viewMode === 'hosts' ? '#3b82f6' : 'var(--bg-secondary)',
                color: viewMode === 'hosts' ? 'white' : 'var(--text-primary)',
                fontSize: '14px',
                cursor: 'pointer',
              }}
            >
              <Grid size={16} />
              Хосты
            </button>
            <button
              onClick={() => setViewMode('all-links')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                backgroundColor: viewMode === 'all-links' ? '#3b82f6' : 'var(--bg-secondary)',
                color: viewMode === 'all-links' ? 'white' : 'var(--text-primary)',
                fontSize: '14px',
                cursor: 'pointer',
              }}
            >
              <List size={16} />
              Все ссылки
            </button>
          </div>
        </div>
      </div>

      {/* Filters and Controls */}
      <div className="card" style={{ marginBottom: '24px' }}>
        {viewMode === 'hosts' ? (
          <div>
            {/* Host Filters */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '16px',
            alignItems: 'center',
            marginBottom: '16px',
          }}>
            {/* URL Filter */}
            <div style={{ flex: '1', minWidth: '200px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: 'var(--text-primary)',
                marginBottom: '4px',
              }}>
                Фильтр по URL
              </label>
              <div style={{ position: 'relative' }}>
                <Search size={16} style={{
                  position: 'absolute',
                  left: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#6b7280',
                }} />
                <input
                  type="text"
                  placeholder="Поиск URL..."
                  value={urlFilter}
                  onChange={(e) => setUrlFilter(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 8px 8px 32px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                  }}
                />
              </div>
            </div>

            {/* Technology Filter */}
            <div style={{ flex: '1', minWidth: '200px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: 'var(--text-primary)',
                marginBottom: '4px',
              }}>
                Фильтр по технологии
              </label>
              <div style={{ position: 'relative' }}>
                <Filter size={16} style={{
                  position: 'absolute',
                  left: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#6b7280',
                }} />
                <select
                  value={techFilter}
                  onChange={(e) => setTechFilter(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 8px 8px 32px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    cursor: 'pointer',
                    height: '38px',
                  }}
                >
                  <option value="">Все технологии</option>
                  {availableTechnologies.map(tech => (
                    <option key={tech} value={tech}>{tech}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* IP Filter */}
            <div style={{ flex: '1', minWidth: '200px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: 'var(--text-primary)',
                marginBottom: '4px',
              }}>
                Фильтр по IP-адресу
              </label>
              <div style={{ position: 'relative' }}>
                <Search size={16} style={{
                  position: 'absolute',
                  left: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#6b7280',
                }} />
                <input
                  type="text"
                  placeholder="Поиск IP-адресов..."
                  value={ipFilter}
                  onChange={(e) => setIpFilter(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 8px 8px 32px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                  }}
                />
              </div>
            </div>

            {/* Tag Filter */}
            <div style={{ flex: '1', minWidth: '200px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: 'var(--text-primary)',
                marginBottom: '4px',
              }}>
                Фильтр по тегам
              </label>
              <div style={{ position: 'relative' }}>
                <Filter size={16} style={{
                  position: 'absolute',
                  left: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#6b7280',
                }} />
                <select
                  value={tagFilter}
                  onChange={(e) => setTagFilter(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 8px 8px 32px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    cursor: 'pointer',
                    height: '38px', // Match height of other inputs
                  }}
                >
                  <option value="">Все теги</option>
                  {availableTags.map(tag => (
                    <option key={tag} value={tag}>{tag}</option>
                  ))}
                </select>
              </div>
            </div>
            <div style={{ flex: '1', minWidth: '150px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px', color: 'var(--text-secondary)' }}>
                  <Filter size={14} style={{ marginRight: 4, verticalAlign: 'text-bottom' }} />
                  Статус
                </label>
                <select
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '14px' }}
                >
                  <option value="">Все статусы</option>
                  <option value="active">Активные</option>
                  <option value="stale">Устаревшие</option>
                  <option value="gone">Недоступные</option>
                  <option value="manual">Ручные</option>
                </select>
              </div>
            </div>
            </div>

            {/* Regex Toggle */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              marginBottom: '16px'
            }}>
              <input
                type="checkbox"
                id="useRegex"
                checked={useRegex}
                onChange={(e) => setUseRegex(e.target.checked)}
                style={{
                  width: '16px',
                  height: '16px',
                  accentColor: '#3b82f6'
                }}
              />
              <label
                htmlFor="useRegex"
                style={{
                  fontSize: '14px',
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                Использовать регулярные выражения
              </label>
            </div>

          </div>
        ) : (
          <>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '16px',
            alignItems: 'center',
            marginBottom: '16px',
          }}>
            {/* Link URL Filter */}
            <div style={{ flex: '1', minWidth: '200px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: 'var(--text-primary)',
                marginBottom: '4px',
              }}>
                Фильтр по URL ссылки
              </label>
              <div style={{ position: 'relative' }}>
                <Search size={16} style={{
                  position: 'absolute',
                  left: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#6b7280',
                }} />
                <input
                  type="text"
                  placeholder="Поиск URL ссылок..."
                  value={linkFilters.urlSubstring}
                  onChange={(e) => setLinkFilters({...linkFilters, urlSubstring: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '8px 8px 8px 32px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                  }}
                />
              </div>
            </div>

            {/* Status Code Filter */}
            <div style={{ flex: '1', minWidth: '150px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: 'var(--text-primary)',
                marginBottom: '4px',
              }}>
                Статус-код
              </label>
              <input
                type="text"
                placeholder="например: 200, 404..."
                value={linkFilters.statusCode}
                onChange={(e) => setLinkFilters({...linkFilters, statusCode: e.target.value})}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                }}
              />
            </div>

            {/* Size Range Filters */}
            <div style={{ flex: '1', minWidth: '150px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: 'var(--text-primary)',
                marginBottom: '4px',
              }}>
                Размер (байт)
              </label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="number"
                  placeholder="Мин."
                  value={linkFilters.sizeMin}
                  onChange={(e) => setLinkFilters({...linkFilters, sizeMin: e.target.value})}
                  style={{
                    flex: 1,
                    padding: '8px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                  }}
                />
                <input
                  type="number"
                  placeholder="Макс."
                  value={linkFilters.sizeMax}
                  onChange={(e) => setLinkFilters({...linkFilters, sizeMax: e.target.value})}
                  style={{
                    flex: 1,
                    padding: '8px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                  }}
                />
              </div>
            </div>

            {/* Technology Filter for Links */}
            <div style={{ flex: '1', minWidth: '200px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: 'var(--text-primary)',
                marginBottom: '4px',
              }}>
                Технологии ссылки
              </label>
              <div style={{ position: 'relative' }}>
                <Filter size={16} style={{
                  position: 'absolute',
                  left: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#6b7280',
                }} />
                <input
                  type="text"
                  placeholder="Поиск технологий ссылки..."
                  value={linkFilters.tech}
                  onChange={(e) => setLinkFilters({...linkFilters, tech: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '8px 8px 8px 32px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                  }}
                />
              </div>
            </div>

            {/* IP Filter for Links */}
            <div style={{ flex: '1', minWidth: '200px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: 'var(--text-primary)',
                marginBottom: '4px',
              }}>
                Фильтр по IP-адресу
              </label>
              <div style={{ position: 'relative' }}>
                <Search size={16} style={{
                  position: 'absolute',
                  left: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#6b7280',
                }} />
                <input
                  type="text"
                  placeholder="Поиск IP-адресов..."
                  value={linkFilters.ip}
                  onChange={(e) => setLinkFilters({...linkFilters, ip: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '8px 8px 8px 32px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                  }}
                />
              </div>
            </div>
          </div>

          {/* Regex Toggle for All Links */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            marginBottom: '16px'
          }}>
            <input
              type="checkbox"
              id="useRegexAllLinks"
              checked={useRegex}
              onChange={(e) => setUseRegex(e.target.checked)}
              style={{
                width: '16px',
                height: '16px',
                accentColor: '#3b82f6'
              }}
            />
            <label
              htmlFor="useRegexAllLinks"
              style={{
                fontSize: '14px',
                color: 'var(--text-primary)',
                cursor: 'pointer',
                userSelect: 'none'
              }}
            >
              Использовать регулярные выражения
            </label>
          </div>
          </>
        )}

        {/* Sort Controls */}
        <div style={{
          display: 'flex',
          gap: '12px',
          alignItems: 'center',
          flexWrap: 'wrap',
          marginBottom: '16px',
        }}>
          <span style={{
            fontSize: '14px',
            fontWeight: '500',
            color: 'var(--text-primary)',
          }}>
            Сортировать по:
          </span>
          {viewMode === 'hosts' ? (
            ['url', 'tech', 'links', 'vulnerabilities', 'ip'].map((field) => (
              <button
                key={field}
                onClick={() => handleSort(field)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '6px 12px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  backgroundColor: sortBy === field ? '#3b82f6' : 'var(--bg-secondary)',
                  color: sortBy === field ? 'white' : 'var(--text-primary)',
                  fontSize: '12px',
                  cursor: 'pointer',
                  textTransform: 'capitalize',
                }}
              >
                {field}
                {sortBy === field && (
                  sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                )}
              </button>
            ))
          ) : (
            ['url', 'host', 'status', 'size', 'tech', 'ip'].map((field) => (
              <button
                key={field}
                onClick={() => handleAllLinksSort(field)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '6px 12px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  backgroundColor: allLinksSortBy === field ? '#3b82f6' : 'var(--bg-secondary)',
                  color: allLinksSortBy === field ? 'white' : 'var(--text-primary)',
                  fontSize: '12px',
                  cursor: 'pointer',
                  textTransform: 'capitalize',
                }}
              >
                {field}
                {allLinksSortBy === field && (
                  allLinksSortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                )}
              </button>
            ))
          )}
        </div>

        {/* Download Controls */}
        <div style={{
          display: 'flex',
          gap: '12px',
          alignItems: 'center',
          flexWrap: 'wrap',
          paddingTop: '16px',
          borderTop: '1px solid var(--border-color)',
        }}>
          <span style={{
            fontSize: '14px',
            fontWeight: '500',
            color: 'var(--text-primary)',
          }}>
            Скачать:
          </span>
          
          {viewMode === 'hosts' ? (
            <>
          {/* All Hosts Downloads */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Все хосты:</span>
            {['json', 'xls'].map((format) => (
              <button
                key={`all-${format}`}
                onClick={() => downloadData(format, 'all')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '6px 12px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  backgroundColor: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  fontSize: '12px',
                  cursor: 'pointer',
                  textTransform: 'uppercase',
                }}
              >
                <Download size={12} />
                {format}
              </button>
            ))}
          </div>

          {/* Filtered Hosts Downloads */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Отфильтровано:</span>
            {['json', 'xls'].map((format) => (
              <button
                key={`filtered-${format}`}
                onClick={() => downloadData(format, 'filtered')}
                disabled={displayedHosts.length === 0}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '6px 12px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  backgroundColor: displayedHosts.length === 0 ? 'var(--bg-tertiary)' : 'var(--bg-secondary)',
                  color: displayedHosts.length === 0 ? 'var(--text-tertiary)' : 'var(--text-primary)',
                  fontSize: '12px',
                  cursor: displayedHosts.length === 0 ? 'not-allowed' : 'pointer',
                  textTransform: 'uppercase',
                  opacity: displayedHosts.length === 0 ? 0.5 : 1,
                }}
              >
                <Download size={12} />
                {format}
              </button>
            ))}
          </div>

          {/* URLs Only Downloads */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Только URL:</span>
            <button
              onClick={() => downloadData('txt', 'urls')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '6px 12px',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                fontSize: '12px',
                cursor: 'pointer',
                textTransform: 'uppercase',
              }}
            >
              <Download size={12} />
              txt
            </button>
          </div>

          {/* All Links Downloads */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Все ссылки:</span>
            <button
              onClick={() => downloadData('txt', 'links')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '6px 12px',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                fontSize: '12px',
                cursor: 'pointer',
                textTransform: 'uppercase',
              }}
            >
              <Download size={12} />
              txt
            </button>
          </div>
            </>
          ) : (
            <>
              {/* All Links Downloads */}
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Все ссылки:</span>
                {['json', 'txt'].map((format) => (
                  <button
                    key={`all-links-${format}`}
                    onClick={() => downloadAllLinksData(format, 'all')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '6px 12px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '6px',
                      backgroundColor: 'var(--bg-secondary)',
                      color: 'var(--text-primary)',
                      fontSize: '12px',
                      cursor: 'pointer',
                      textTransform: 'uppercase',
                    }}
                  >
                    <Download size={12} />
                    {format}
                  </button>
                ))}
              </div>

              {/* Filtered Links Downloads */}
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Отфильтрованные ссылки:</span>
                {['json', 'txt'].map((format) => (
                  <button
                    key={`filtered-links-${format}`}
                    onClick={() => downloadAllLinksData(format, 'filtered')}
                    disabled={allLinksTotal === 0}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '6px 12px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '6px',
                      backgroundColor: allLinksTotal === 0 ? 'var(--bg-tertiary)' : 'var(--bg-secondary)',
                      color: allLinksTotal === 0 ? 'var(--text-tertiary)' : 'var(--text-primary)',
                      fontSize: '12px',
                      cursor: allLinksTotal === 0 ? 'not-allowed' : 'pointer',
                      textTransform: 'uppercase',
                      opacity: allLinksTotal === 0 ? 0.5 : 1,
                    }}
                  >
                    <Download size={12} />
                    {format}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <div>
        {viewMode === 'hosts' ? (
          /* Hosts List */
          <div>
          <div className="card">
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: 'var(--text-primary)',
              marginBottom: '16px',
            }}>
              Все хосты
            </h3>

            <div style={{ overflowX: 'auto' }}>
              <table className="table" style={{ tableLayout: 'fixed', width: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ cursor: 'pointer', width: hostsColumnWidths.url, position: 'relative' }} onClick={() => handleSort('url')}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        URL
                        {sortBy === 'url' && (
                          sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                        )}
                      </div>
                      <div
                        onMouseDown={(e) => handleResizeStart('hosts', 'url', e)}
                        style={{
                          position: 'absolute',
                          right: 0,
                          top: 0,
                          bottom: 0,
                          width: '4px',
                          cursor: 'col-resize',
                          backgroundColor: resizingColumn?.table === 'hosts' && resizingColumn?.columnKey === 'url' ? '#3b82f6' : 'transparent',
                          zIndex: 1
                        }}
                      />
                    </th>
                    <th style={{ width: hostsColumnWidths.tags, position: 'relative' }}>Теги
                      <div
                        onMouseDown={(e) => handleResizeStart('hosts', 'tags', e)}
                        style={{
                          position: 'absolute',
                          right: 0,
                          top: 0,
                          bottom: 0,
                          width: '4px',
                          cursor: 'col-resize',
                          backgroundColor: resizingColumn?.table === 'hosts' && resizingColumn?.columnKey === 'tags' ? '#3b82f6' : 'transparent',
                          zIndex: 1
                        }}
                      />
                    </th>
                    <th style={{ cursor: 'pointer', width: hostsColumnWidths.tech, position: 'relative' }} onClick={() => handleSort('tech')}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        Технологии
                        {sortBy === 'tech' && (
                          sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                        )}
                      </div>
                      <div
                        onMouseDown={(e) => handleResizeStart('hosts', 'tech', e)}
                        style={{
                          position: 'absolute',
                          right: 0,
                          top: 0,
                          bottom: 0,
                          width: '4px',
                          cursor: 'col-resize',
                          backgroundColor: resizingColumn?.table === 'hosts' && resizingColumn?.columnKey === 'tech' ? '#3b82f6' : 'transparent',
                          zIndex: 1
                        }}
                      />
                    </th>
                    <th style={{ cursor: 'pointer', width: hostsColumnWidths.links, position: 'relative' }} onClick={() => handleSort('links')}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        Локации
                        {sortBy === 'links' && (
                          sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                        )}
                      </div>
                      <div
                        onMouseDown={(e) => handleResizeStart('hosts', 'links', e)}
                        style={{
                          position: 'absolute',
                          right: 0,
                          top: 0,
                          bottom: 0,
                          width: '4px',
                          cursor: 'col-resize',
                          backgroundColor: resizingColumn?.table === 'hosts' && resizingColumn?.columnKey === 'links' ? '#3b82f6' : 'transparent',
                          zIndex: 1
                        }}
                      />
                    </th>
                    <th style={{ cursor: 'pointer', width: hostsColumnWidths.vulnerabilities, position: 'relative' }} onClick={() => handleSort('vulnerabilities')}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Shield size={14} />
                        Уязвимости
                        {sortBy === 'vulnerabilities' && (
                          sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                        )}
                      </div>
                      <div
                        onMouseDown={(e) => handleResizeStart('hosts', 'vulnerabilities', e)}
                        style={{
                          position: 'absolute',
                          right: 0,
                          top: 0,
                          bottom: 0,
                          width: '4px',
                          cursor: 'col-resize',
                          backgroundColor: resizingColumn?.table === 'hosts' && resizingColumn?.columnKey === 'vulnerabilities' ? '#3b82f6' : 'transparent',
                          zIndex: 1
                        }}
                      />
                    </th>
                    <th style={{ width: hostsColumnWidths.comments, position: 'relative' }}>Комментарии
                      <div
                        onMouseDown={(e) => handleResizeStart('hosts', 'comments', e)}
                        style={{
                          position: 'absolute',
                          right: 0,
                          top: 0,
                          bottom: 0,
                          width: '4px',
                          cursor: 'col-resize',
                          backgroundColor: resizingColumn?.table === 'hosts' && resizingColumn?.columnKey === 'comments' ? '#3b82f6' : 'transparent',
                          zIndex: 1
                        }}
                      />
                    </th>
                    <th style={{ cursor: 'pointer', width: hostsColumnWidths.ip, position: 'relative' }} onClick={() => handleSort('ip')}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        IP-адрес
                        {sortBy === 'ip' && (
                          sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                        )}
                      </div>
                      <div
                        onMouseDown={(e) => handleResizeStart('hosts', 'ip', e)}
                        style={{
                          position: 'absolute',
                          right: 0,
                          top: 0,
                          bottom: 0,
                          width: '4px',
                          cursor: 'col-resize',
                          backgroundColor: resizingColumn?.table === 'hosts' && resizingColumn?.columnKey === 'ip' ? '#3b82f6' : 'transparent',
                          zIndex: 1
                        }}
                      />
                    </th>
                    <th style={{ width: hostsColumnWidths.actions }}>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedHosts.map((host) => (
                    <React.Fragment key={host._id}>
                      <tr>
                        <td style={{ width: hostsColumnWidths.url, overflow: 'hidden' }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                          }}>
                            <Server size={16} color="#6b7280" />
                            {host.status && host.status !== 'active' && (
                              <span style={{
                                padding: '1px 5px',
                                borderRadius: '8px',
                                fontSize: '10px',
                                fontWeight: '600',
                                backgroundColor: host.status === 'stale' ? '#fef3c7' : host.status === 'gone' ? '#fee2e2' : '#dbeafe',
                                color: host.status === 'stale' ? '#92400e' : host.status === 'gone' ? '#991b1b' : '#1e40af',
                                flexShrink: 0,
                              }}>
                                {host.status}
                              </span>
                            )}
                            {host.host ? (
                              <a
                                href={`${getHostProtocol(host)}://${host.host}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  color: '#3b82f6',
                                  textDecoration: 'none',
                                  cursor: 'pointer',
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                                onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                                title={`Открыть ${host.host} в новой вкладке`}
                              >
                                {host.host}
                              </a>
                            ) : (
                              <span style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}>
                                Н/Д
                              </span>
                            )}
                          </div>
                        </td>
                        <td style={{ width: hostsColumnWidths.tags, overflow: 'hidden' }}>
                          <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '4px',
                          }}>
                            {host.tags && host.tags.length > 0 ? (
                              host.tags.slice(0, 3).map((tag, idx) => {
                                const color = getTagColor(idx);
                                return (
                                  <span
                                    key={idx}
                                    style={{
                                      backgroundColor: color.bg,
                                      color: color.text,
                                      border: `1px solid ${color.border}`,
                                      padding: '2px 6px',
                                      borderRadius: '10px',
                                      fontSize: '11px',
                                      fontWeight: '500',
                                    }}
                                  >
                                    {tag}
                                  </span>
                                );
                              })
                            ) : (
                              <span style={{ color: '#9ca3af', fontSize: '12px' }}>-</span>
                            )}
                            {host.tags && host.tags.length > 3 && (
                              <span style={{
                                color: '#6b7280',
                                fontSize: '11px',
                                fontWeight: '500',
                              }}>
                                +{host.tags.length - 3}
                              </span>
                            )}
                          </div>
                        </td>
                        <td style={{ width: hostsColumnWidths.tech, overflow: 'hidden' }}>
                          <span style={{
                            backgroundColor: '#eff6ff',
                            color: '#1d4ed8',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                          }}>
                            {formatTechnologies(host.tech)}
                          </span>
                        </td>
                        <td style={{ width: hostsColumnWidths.links, overflow: 'hidden' }}>
                          <span style={{
                            backgroundColor: '#f0fdf4',
                            color: '#16a34a',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                          }}>
                            {getHostLocations(host).length}
                          </span>
                        </td>
                        <td style={{ width: hostsColumnWidths.vulnerabilities, overflow: 'hidden' }}>
                          {(() => {
                            const stats = host.vulnerability_stats || { total: 0 };
                            const color = getVulnerabilityBadgeColor(stats);
                            return (
                              <div style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '28px',
                                height: '28px',
                                borderRadius: '50%',
                                backgroundColor: color,
                                color: 'white',
                                fontSize: '12px',
                                fontWeight: '600',
                              }}>
                                {stats.total || 0}
                              </div>
                            );
                          })()}
                        </td>
                        <td style={{ width: hostsColumnWidths.comments, overflow: 'hidden' }}>
                          <span style={{
                            backgroundColor: '#f3e8ff',
                            color: '#6b21a8',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                          }}>
                            💬 {host.comment_count || 0}
                          </span>
                        </td>
                  <td style={{ width: hostsColumnWidths.ip, overflow: 'hidden' }}>
                    <span style={{
                      backgroundColor: '#fef2f2',
                      color: '#991b1b',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                    }}>
                      {getFirstIpFromHost(host) || 'Н/Д'}
                          </span>
                        </td>
                        <td style={{ width: hostsColumnWidths.actions, overflow: 'hidden' }}>
                          <button
                            onClick={() => toggleHostExpansion(host._id)}
                            className="btn btn-primary"
                            style={{ padding: '4px 8px', fontSize: '12px' }}
                          >
                            {expandedHosts.has(host._id) ? (
                              <>
                                <EyeOff size={12} style={{ marginRight: '4px' }} />
                                Скрыть
                              </>
                            ) : (
                              <>
                                <Eye size={12} style={{ marginRight: '4px' }} />
                                Показать
                              </>
                            )}
                          </button>
                        </td>
                      </tr>
                      
                      {/* Expanded Host Details */}
                      {expandedHosts.has(host._id) && (
                        <tr>
                          <td colSpan="8" style={{ padding: '0' }}>
                            <div style={{
                              backgroundColor: 'var(--bg-tertiary)',
                              padding: '16px',
                              borderTop: '1px solid var(--border-color)',
                            }}>
                              <div style={{ marginBottom: '16px' }}>
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  marginBottom: '8px',
                                }}>
                                  <ExternalLink size={16} color="#3b82f6" />
                                  <strong>URL:</strong>
                                  <a
                                    href={`${getHostProtocol(host)}://${host.host}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '4px',
                                      color: '#3b82f6',
                                      textDecoration: 'none',
                                      fontSize: '14px',
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                                    onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                                  >
                                    <ExternalLink size={14} />
                                    Открыть в новой вкладке
                                  </a>
                                </div>
                                <div style={{
                                  backgroundColor: 'var(--bg-secondary)',
                                  color: 'var(--text-primary)',
                                  padding: '8px',
                                  borderRadius: '6px',
                                  fontSize: '14px',
                                  wordBreak: 'break-all',
                                  border: '1px solid var(--border-color)',
                                }}>
                                  {host.host || 'Н/Д'}
                                </div>
                                <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                                  <button
                                    onClick={() => openEditHostModal(host)}
                                    className="btn btn-secondary"
                                    style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                                  >
                                    <Pencil size={12} />
                                    Изменить хост
                                  </button>
                                  <button
                                    onClick={() => deleteHostById(host)}
                                    className="btn btn-danger"
                                    style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                                  >
                                    <Trash2 size={12} />
                                    Удалить хост
                                  </button>
                                </div>
                              </div>

                              {/* Tags Management Section */}
                              <div style={{ marginBottom: '16px' }}>
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  marginBottom: '8px',
                                }}>
                                  <strong>Теги:</strong>
                                </div>
                                <div style={{
                                  backgroundColor: 'var(--bg-secondary)',
                                  padding: '12px',
                                  borderRadius: '6px',
                                  border: '1px solid var(--border-color)',
                                }}>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
                                    {host.tags && host.tags.length > 0 ? (
                                      host.tags.map((tag, idx) => {
                                        const color = getTagColor(idx);
                                        return (
                                          <div
                                            key={idx}
                                            style={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: '4px',
                                              backgroundColor: color.bg,
                                              color: color.text,
                                              border: `1px solid ${color.border}`,
                                              padding: '4px 8px',
                                              borderRadius: '12px',
                                              fontSize: '12px',
                                              fontWeight: '500',
                                            }}
                                          >
                                            <span>{tag}</span>
                                            <button
                                              onClick={() => removeTagFromHost(host._id, tag)}
                                              style={{
                                                background: 'none',
                                                border: 'none',
                                                cursor: 'pointer',
                                                padding: '0',
                                                color: color.text,
                                                fontSize: '14px',
                                                fontWeight: 'bold',
                                                lineHeight: '1',
                                              }}
                                            >
                                              ×
                                            </button>
                                          </div>
                                        );
                                      })
                                    ) : (
                                      <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>Нет тегов</span>
                                    )}
                                  </div>
                                  <div style={{ display: 'flex', gap: '8px' }}>
                                    <input
                                      type="text"
                                      placeholder="Добавить тег..."
                                      value={hostNewTag[host._id] || ''}
                                      onChange={(e) => setHostNewTag(prev => ({ ...prev, [host._id]: e.target.value }))}
                                      onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                          addTagToHost(host._id);
                                        }
                                      }}
                                      style={{
                                        flex: 1,
                                        padding: '6px 10px',
                                        border: '1px solid var(--border-color)',
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        backgroundColor: 'var(--bg-tertiary)',
                                        color: 'var(--text-primary)',
                                      }}
                                    />
                                    <button
                                      onClick={() => addTagToHost(host._id)}
                                      style={{
                                        padding: '6px 12px',
                                        border: 'none',
                                        borderRadius: '4px',
                                        backgroundColor: '#10b981',
                                        color: 'white',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                      }}
                                    >
                                      + Добавить
                                    </button>
                                  </div>
                                </div>
                              </div>

                              <div style={{ marginBottom: '16px' }}>
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  marginBottom: '8px',
                                }}>
                                  <Cpu size={16} color="#f59e0b" />
                                  <strong>Технологии ({host.tech?.length || 0}):</strong>
                                </div>
                                <div style={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: '6px',
                                }}>
                                  {host.tech && Array.isArray(host.tech) && host.tech.map((tech, index) => (
                                    <span
                                      key={index}
                                      style={{
                                        backgroundColor: '#fef3c7',
                                        color: '#92400e',
                                        padding: '4px 8px',
                                        borderRadius: '12px',
                                        fontSize: '12px',
                                      }}
                                    >
                                      {tech || 'Неизвестно'}
                                    </span>
                                  ))}
                                </div>
                              </div>

                              {/* Vulnerabilities Section */}
                              <div style={{ marginBottom: '16px' }}>
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  marginBottom: '8px',
                                }}>
                                  <Shield size={16} color="#dc2626" />
                                  <strong>Уязвимости:</strong>
                                </div>
                                <div style={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: '8px',
                                }}>
                                  {(() => {
                                    const stats = host.vulnerability_stats || {
                                      critical: 0,
                                      high: 0,
                                      medium: 0,
                                      low: 0,
                                      info: 0,
                                      total: 0
                                    };
                                    
                                    const badges = [
                                      { label: 'Критические', count: stats.critical, severity: 'critical', color: '#8b0000', bgColor: '#fee2e2' },
                                      { label: 'Высокие', count: stats.high, severity: 'high', color: '#ea580c', bgColor: '#ffedd5' },
                                      { label: 'Средние', count: stats.medium, severity: 'medium', color: '#f59e0b', bgColor: '#fef3c7' },
                                      { label: 'Низкие', count: stats.low, severity: 'low', color: '#10b981', bgColor: '#d1fae5' },
                                    ];
                                    
                                    // Only show Info if count > 0
                                    if (stats.info > 0) {
                                      badges.push({ label: 'Инфо', count: stats.info, severity: 'info', color: '#3b82f6', bgColor: '#dbeafe' });
                                    }
                                    
                                    return badges.map((badge) => (
                                      <div
                                        key={badge.severity}
                                        onClick={() => navigateToVulnerabilities(host.host, badge.severity)}
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '6px',
                                          padding: '6px 12px',
                                          backgroundColor: badge.bgColor,
                                          color: badge.color,
                                          border: `1px solid ${badge.color}`,
                                          borderRadius: '6px',
                                          fontSize: '13px',
                                          fontWeight: '600',
                                          cursor: 'pointer',
                                          transition: 'all 0.2s',
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.backgroundColor = badge.color;
                                          e.currentTarget.style.color = 'white';
                                          e.currentTarget.style.transform = 'scale(1.05)';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.backgroundColor = badge.bgColor;
                                          e.currentTarget.style.color = badge.color;
                                          e.currentTarget.style.transform = 'scale(1)';
                                        }}
                                        title={`Нажмите, чтобы посмотреть уязвимости уровня "${badge.label.toLowerCase()}" для этого хоста`}
                                      >
                                        <span>{badge.label}:</span>
                                        <span>{badge.count}</span>
                                      </div>
                                    ));
                                  })()}
                                  {(!host.vulnerability_stats || host.vulnerability_stats.total === 0) && (
                                    <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>Уязвимости отсутствуют</span>
                                  )}
                                </div>
                              </div>

                              {/* Comments - Collapsible */}
                              <div style={{ marginBottom: '16px' }}>
                                <button
                                  onClick={() => toggleCommentsSection(host._id)}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    marginBottom: '8px',
                                    background: 'none',
                                    border: 'none',
                                    padding: '4px 8px',
                                    cursor: 'pointer',
                                    color: 'var(--text-primary)',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    borderRadius: '4px',
                                    transition: 'background 0.2s',
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
                                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                >
                                  {collapsedCommentsSections.has(host._id) ? (
                                    <ChevronDown size={16} />
                                  ) : (
                                    <ChevronUp size={16} />
                                  )}
                                  <strong>Комментарии ({host.comment_count || 0})</strong>
                                </button>
                                
                                {!collapsedCommentsSections.has(host._id) && (
                                  <div style={{ background: 'var(--bg-secondary)', padding: 12, borderRadius: 6, border: '1px solid var(--border-color)' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 8 }}>
                                      <textarea
                                        value={hostNewComment[host._id] || ''}
                                        onChange={e => setHostNewComment(prev => ({ ...prev, [host._id]: e.target.value }))}
                                        placeholder="Напишите комментарий (поддерживается Markdown)"
                                        className="form-textarea"
                                        rows={3}
                                        style={{ flex: 1, resize: 'vertical' }}
                                      />
                                      <button className="btn btn-primary" onClick={() => postHostComment(host._id)} style={{ alignSelf: 'flex-end' }}>Добавить комментарий</button>
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                      {(hostComments[host._id]?.items || []).map(cm => (
                                        <div key={cm._id} style={{ background: 'var(--bg-tertiary)', padding: 8, borderRadius: 6 }}>
                                          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 4 }}>
                                            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                                              {cm.author?.username} • {cm.created_at ? new Date(cm.created_at).toLocaleString() : ''} {cm.edited_at ? '(изменён)' : ''}
                                            </div>
                                            <div style={{ display:'flex', gap:8 }}>
                                              {hostEditingId[host._id] === cm._id ? (
                                                <>
                                                  <button onClick={() => saveHostComment(host._id)}>Сохранить</button>
                                                  <button onClick={() => cancelEditHostComment(host._id)}>Отмена</button>
                                                </>
                                              ) : (
                                                <>
                                                  <button className="btn btn-secondary" onClick={() => startEditHostComment(host._id, cm)}>Изменить</button>
                                                  <button className="btn btn-danger" onClick={() => deleteHostComment(host._id, cm._id)}>Удалить</button>
                                                </>
                                              )}
                                            </div>
                                          </div>
                                          {hostEditingId[host._id] === cm._id ? (
                                            <textarea
                                              value={hostEditingBody[host._id] || ''}
                                              onChange={e => setHostEditingBody(prev => ({ ...prev, [host._id]: e.target.value }))}
                                              rows={3}
                                              className="form-textarea"
                                            />
                                          ) : (
                                            <div style={{ color: 'var(--text-primary)' }}>
                                              <ReactMarkdown>{cm.body_md || ''}</ReactMarkdown>
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                        <button className="btn" disabled={(hostComments[host._id]?.page || 1) <= 1} onClick={() => loadHostComments(host._id, (hostComments[host._id]?.page || 1) - 1)}>Назад</button>
                                        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Страница {hostComments[host._id]?.page || 1}</div>
                                        <button className="btn" disabled={((hostComments[host._id]?.page || 1) * 10) >= (hostComments[host._id]?.total || 0)} onClick={() => loadHostComments(host._id, (hostComments[host._id]?.page || 1) + 1)}>Вперёд</button>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>

                              <div>
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  marginBottom: '8px',
                                }}>
                                  <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                }}>
                                  <LinkIcon size={16} color="#10b981" />
                                    <strong>Локации ({getFilteredAndSortedHostLinks(host).length} из {getHostLocations(host).length}):</strong>
                                  </div>
                                  {getFilteredAndSortedHostLinks(host).length > 0 && (
                                    <button
                                      onClick={() => downloadHostLinks(host)}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        padding: '4px 8px',
                                        border: '1px solid var(--border-color)',
                                        borderRadius: '4px',
                                        backgroundColor: 'var(--bg-secondary)',
                                        color: 'var(--text-primary)',
                                        fontSize: '11px',
                                        cursor: 'pointer',
                                      }}
                                    >
                                      <Download size={12} />
                                      Скачать JSON
                                    </button>
                                  )}
                                </div>

                                {/* Individual Host Links Filters */}
                                <div style={{
                                  backgroundColor: 'var(--bg-quaternary)',
                                  padding: '12px',
                                  borderRadius: '6px',
                                  marginBottom: '12px',
                                  border: '1px solid var(--border-color)',
                                }}>
                                  <div style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '12px',
                                    alignItems: 'center',
                                    marginBottom: '8px',
                                  }}>
                                    {/* Link URL Filter */}
                                    <div style={{ flex: '1', minWidth: '150px' }}>
                                      <label style={{
                                        display: 'block',
                                        fontSize: '10px',
                                        fontWeight: '500',
                                        color: 'var(--text-secondary)',
                                        marginBottom: '2px',
                                      }}>
                                        URL ссылки
                                      </label>
                                      <div style={{ position: 'relative' }}>
                                        <Search size={12} style={{
                                          position: 'absolute',
                                          left: '4px',
                                          top: '50%',
                                          transform: 'translateY(-50%)',
                                          color: '#6b7280',
                                        }} />
                                        <input
                                          type="text"
                                          placeholder="Поиск URL..."
                                          value={getHostSettings(host._id).filters.urlSubstring}
                                          onChange={(e) => updateHostSettings(host._id, {
                                            filters: { ...getHostSettings(host._id).filters, urlSubstring: e.target.value }
                                          })}
                                          style={{
                                            width: '100%',
                                            padding: '4px 4px 4px 20px',
                                            border: '1px solid var(--border-color)',
                                            borderRadius: '3px',
                                            fontSize: '10px',
                                            backgroundColor: 'var(--bg-secondary)',
                                            color: 'var(--text-primary)',
                                          }}
                                        />
                                      </div>
                                    </div>

                                    {/* Status Code Filter */}
                                    <div style={{ flex: '1', minWidth: '80px' }}>
                                      <label style={{
                                        display: 'block',
                                        fontSize: '10px',
                                        fontWeight: '500',
                                        color: 'var(--text-secondary)',
                                        marginBottom: '2px',
                                      }}>
                                        Статус
                                      </label>
                                      <input
                                        type="text"
                                        placeholder="200, 404..."
                                        value={getHostSettings(host._id).filters.statusCode}
                                        onChange={(e) => updateHostSettings(host._id, {
                                          filters: { ...getHostSettings(host._id).filters, statusCode: e.target.value }
                                        })}
                                        style={{
                                          width: '100%',
                                          padding: '4px',
                                          border: '1px solid var(--border-color)',
                                          borderRadius: '3px',
                                          fontSize: '10px',
                                          backgroundColor: 'var(--bg-secondary)',
                                          color: 'var(--text-primary)',
                                        }}
                                      />
                                    </div>

                                    {/* Size Range Filters */}
                                    <div style={{ flex: '1', minWidth: '120px' }}>
                                      <label style={{
                                        display: 'block',
                                        fontSize: '10px',
                                        fontWeight: '500',
                                        color: 'var(--text-secondary)',
                                        marginBottom: '2px',
                                      }}>
                                        Размер (байт)
                                      </label>
                                      <div style={{ display: 'flex', gap: '2px' }}>
                                        <input
                                          type="number"
                                          placeholder="Мин."
                                          value={getHostSettings(host._id).filters.sizeMin}
                                          onChange={(e) => updateHostSettings(host._id, {
                                            filters: { ...getHostSettings(host._id).filters, sizeMin: e.target.value }
                                          })}
                                          style={{
                                            flex: 1,
                                            padding: '4px',
                                            border: '1px solid var(--border-color)',
                                            borderRadius: '3px',
                                            fontSize: '10px',
                                            backgroundColor: 'var(--bg-secondary)',
                                            color: 'var(--text-primary)',
                                          }}
                                        />
                                        <input
                                          type="number"
                                          placeholder="Макс."
                                          value={getHostSettings(host._id).filters.sizeMax}
                                          onChange={(e) => updateHostSettings(host._id, {
                                            filters: { ...getHostSettings(host._id).filters, sizeMax: e.target.value }
                                          })}
                                          style={{
                                            flex: 1,
                                            padding: '4px',
                                            border: '1px solid var(--border-color)',
                                            borderRadius: '3px',
                                            fontSize: '10px',
                                            backgroundColor: 'var(--bg-secondary)',
                                            color: 'var(--text-primary)',
                                          }}
                                        />
                                      </div>
                                    </div>

                                    {/* Technology Filter */}
                                    <div style={{ flex: '1', minWidth: '120px' }}>
                                      <label style={{
                                        display: 'block',
                                        fontSize: '10px',
                                        fontWeight: '500',
                                        color: 'var(--text-secondary)',
                                        marginBottom: '2px',
                                      }}>
                                        Технологии
                                      </label>
                                      <div style={{ position: 'relative' }}>
                                        <Filter size={12} style={{
                                          position: 'absolute',
                                          left: '4px',
                                          top: '50%',
                                          transform: 'translateY(-50%)',
                                          color: '#6b7280',
                                        }} />
                                        <input
                                          type="text"
                                          placeholder="Поиск технологий..."
                                          value={getHostSettings(host._id).filters.tech}
                                          onChange={(e) => updateHostSettings(host._id, {
                                            filters: { ...getHostSettings(host._id).filters, tech: e.target.value }
                                          })}
                                          style={{
                                            width: '100%',
                                            padding: '4px 4px 4px 20px',
                                            border: '1px solid var(--border-color)',
                                            borderRadius: '3px',
                                            fontSize: '10px',
                                            backgroundColor: 'var(--bg-secondary)',
                                            color: 'var(--text-primary)',
                                          }}
                                        />
                                      </div>
                                    </div>
                                  </div>

                                  {/* Sort Controls */}
                                  <div style={{
                                    display: 'flex',
                                    gap: '6px',
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                  }}>
                                    <span style={{
                                      fontSize: '10px',
                                      fontWeight: '500',
                                      color: 'var(--text-secondary)',
                                    }}>
                                      Сортировать по:
                                    </span>
                                    {['url', 'status', 'size', 'tech'].map((field) => (
                                      <button
                                        key={field}
                                        onClick={() => handleHostLinksSort(host._id, field)}
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '2px',
                                          padding: '2px 6px',
                                          border: '1px solid var(--border-color)',
                                          borderRadius: '3px',
                                          backgroundColor: getHostSettings(host._id).sortBy === field ? '#3b82f6' : 'var(--bg-secondary)',
                                          color: getHostSettings(host._id).sortBy === field ? 'white' : 'var(--text-primary)',
                                          fontSize: '9px',
                                          cursor: 'pointer',
                                          textTransform: 'capitalize',
                                        }}
                                      >
                                        {field}
                                        {getHostSettings(host._id).sortBy === field && (
                                          getHostSettings(host._id).sortOrder === 'asc' ? <ChevronUp size={8} /> : <ChevronDown size={8} />
                                        )}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                                <div style={{
                                  maxHeight: '200px',
                                  overflowY: 'auto',
                                  backgroundColor: 'var(--bg-secondary)',
                                  color: 'var(--text-primary)',
                                  padding: '8px',
                                  borderRadius: '6px',
                                  border: '1px solid var(--border-color)',
                                }}>
                                  {(() => {
                                    const paginated = getPaginatedHostLinks(host);
                                    return paginated.links.map((linkObj, index) => (
                                      <div
                                        key={linkObj.id || index}
                                        style={{
                                          padding: '6px',
                                          fontSize: '12px',
                                          borderBottom: index < paginated.links.length - 1 ? '1px solid var(--border-color)' : 'none',
                                          wordBreak: 'break-all',
                                          backgroundColor: 'var(--bg-tertiary)',
                                          borderRadius: '4px',
                                          marginBottom: '4px',
                                        }}
                                      >
                                        <div style={{ fontWeight: '500', marginBottom: '2px' }}>
                                          {linkObj.url || 'Н/Д'}
                                        </div>
                                        <div style={{ 
                                          display: 'flex', 
                                          gap: '8px', 
                                          fontSize: '11px',
                                          color: 'var(--text-secondary)'
                                        }}>
                                          {linkObj.status_code && (
                                            <span style={{ 
                                              color: getStatusColor(linkObj.status_code),
                                              fontWeight: '500'
                                            }}>
                                              Статус: {linkObj.status_code}
                                            </span>
                                          )}
                                          {linkObj.size && (
                                            <span>Размер: {linkObj.size} байт</span>
                                          )}
                                          {linkObj.tech && linkObj.tech.length > 0 && (
                                            <span>Тех.: {linkObj.tech.join(', ')}</span>
                                          )}
                                          {linkObj.ip && (
                                            <span style={{ color: '#991b1b', fontWeight: '500' }}>IP: {linkObj.ip}</span>
                                          )}
                                        </div>
                                      </div>
                                    ));
                                  })()}
                                  {getFilteredAndSortedHostLinks(host).length === 0 && (
                                    <div style={{
                                      textAlign: 'center',
                                      color: 'var(--text-secondary)',
                                      fontSize: '12px',
                                      padding: '16px',
                                    }}>
                                      Нет ссылок, соответствующих текущим фильтрам
                                    </div>
                                  )}
                                </div>
                                {(() => {
                                  const paginated = getPaginatedHostLinks(host);
                                  if (paginated.totalPages > 1) {
                                    return (
                                      <div style={{ 
                                        display: 'flex', 
                                        gap: 8, 
                                        alignItems: 'center', 
                                        marginTop: '12px',
                                        justifyContent: 'space-between'
                                      }}>
                                        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                                          Показано {paginated.startIndex}-{paginated.endIndex} из {paginated.total} локаций
                                        </div>
                                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                          <button 
                                            className="btn" 
                                            disabled={paginated.currentPage <= 1} 
                                            onClick={() => setHostLocationsPages(prev => ({ ...prev, [host._id]: paginated.currentPage - 1 }))}
                                            style={{ padding: '4px 12px', fontSize: '12px' }}
                                          >
                                            Назад
                                          </button>
                                          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                                            Страница {paginated.currentPage} из {paginated.totalPages}
                                          </div>
                                          <button 
                                            className="btn" 
                                            disabled={paginated.currentPage >= paginated.totalPages} 
                                            onClick={() => setHostLocationsPages(prev => ({ ...prev, [host._id]: paginated.currentPage + 1 }))}
                                            style={{ padding: '4px 12px', fontSize: '12px' }}
                                          >
                                            Вперёд
                                          </button>
                                        </div>
                                      </div>
                                    );
                                  }
                                  return null;
                                })()}
                              </div>

                              {/* Domains Section */}
                              {getHostDomains(host).length > 0 && (
                                <div style={{ marginTop: '16px' }}>
                                  <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    marginBottom: '8px',
                                  }}>
                                    <Server size={16} color="#8b5cf6" />
                                    <strong>Домены ({getHostDomains(host).length}):</strong>
                                  </div>
                                  <div style={{
                                    maxHeight: '150px',
                                    overflowY: 'auto',
                                    backgroundColor: 'var(--bg-secondary)',
                                    color: 'var(--text-primary)',
                                    padding: '8px',
                                    borderRadius: '6px',
                                    border: '1px solid var(--border-color)',
                                  }}>
                                    {getHostDomains(host).map((domain, index) => (
                                      <div
                                        key={index}
                                        style={{
                                          padding: '6px',
                                          fontSize: '12px',
                                          borderBottom: index < getHostDomains(host).length - 1 ? '1px solid var(--border-color)' : 'none',
                                          wordBreak: 'break-all',
                                          backgroundColor: 'var(--bg-tertiary)',
                                          borderRadius: '4px',
                                          marginBottom: '4px',
                                        }}
                                      >
                                        <div 
                                          style={{ 
                                            fontWeight: '500', 
                                            marginBottom: '2px',
                                            color: hoveredDomain === domain ? '#2563eb' : '#3b82f6',
                                            cursor: 'pointer',
                                            textDecoration: 'underline',
                                            transition: 'color 0.2s ease',
                                          }}
                                          onClick={() => navigateToHostByDomain(domain)}
                                          onMouseEnter={() => setHoveredDomain(domain)}
                                          onMouseLeave={() => setHoveredDomain(null)}
                                          title={`Нажмите, чтобы отфильтровать хосты по ${domain}`}
                                        >
                                          {domain || 'Н/Д'}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Relations Section */}
                              {/* Classification Section */}
                              <div style={{ marginTop: '16px' }}>
                                <button
                                  onClick={() => toggleClassificationSection(host._id, host.host)}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    marginBottom: '8px',
                                    background: 'none',
                                    border: 'none',
                                    padding: '4px 8px',
                                    cursor: 'pointer',
                                    color: 'var(--text-primary)',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    borderRadius: '4px',
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
                                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                >
                                  {collapsedClassificationSections.has(host._id) ? (
                                    <ChevronDown size={16} />
                                  ) : (
                                    <ChevronUp size={16} />
                                  )}
                                  <strong>Классификация</strong>
                                </button>
                                {!collapsedClassificationSections.has(host._id) && (
                                  <div style={{ background: 'var(--bg-secondary)', padding: 12, borderRadius: 6, border: '1px solid var(--border-color)' }}>
                                    {!hostClassification[host._id] ? (
                                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                                        {hostClassification[host._id] === null ? 'Классификация отсутствует' : 'Загрузка...'}
                                      </div>
                                    ) : (
                                      <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                                        {Object.entries(hostClassification[host._id]?.classification || {}).map(([cat, items]) => (
                                          <div key={cat} style={{ marginBottom: '12px' }}>
                                            <div style={{ fontWeight: 600, marginBottom: '6px', color: 'var(--text-primary)' }}>
                                              - {cat}
                                            </div>
                                            {(items || []).length > 0 ? (
                                              <div style={{ paddingLeft: '20px' }}>
                                                {(items || []).map((u, idx) => (
                                                  <div key={idx} style={{ marginBottom: '4px', color: 'var(--text-secondary)', wordBreak: 'break-all' }}>
                                                    - {u}
                                                  </div>
                                                ))}
                                              </div>
                                            ) : (
                                              <div style={{ paddingLeft: '20px', color: 'var(--text-secondary)', fontStyle: 'italic', fontSize: '12px' }}>
                                                (пусто)
                                              </div>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>

                              {/* Healthcheck History */}
                              <div style={{ marginTop: '12px' }}>
                                <button
                                  onClick={() => toggleHealthcheckSection(host._id, host.host)}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    marginBottom: '8px',
                                    background: 'none',
                                    border: 'none',
                                    padding: '4px 8px',
                                    cursor: 'pointer',
                                    color: 'var(--text-primary)',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    borderRadius: '4px',
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
                                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                >
                                  {collapsedHealthcheckSections.has(host._id) ? (
                                    <ChevronUp size={16} />
                                  ) : (
                                    <ChevronDown size={16} />
                                  )}
                                  <strong>История проверок</strong>
                                </button>
                                {collapsedHealthcheckSections.has(host._id) && (
                                  <div style={{ background: 'var(--bg-secondary)', padding: 12, borderRadius: 6, border: '1px solid var(--border-color)' }}>
                                    {!hostHealthcheckHistory[host._id] ? (
                                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Загрузка...</div>
                                    ) : hostHealthcheckHistory[host._id].length === 0 ? (
                                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Нет записей о проверках</div>
                                    ) : (
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        {hostHealthcheckHistory[host._id].map((item, idx) => {
                                          const changes = item.changes || {};
                                          let badgeCls = 'badge-neutral';
                                          let badgeLabel = 'Изменение';
                                          if (changes.status_recovered) { badgeCls = 'badge-info'; badgeLabel = 'Восстановлен'; }
                                          else if (changes.removed_host) { badgeCls = 'badge-danger'; badgeLabel = 'Недоступен'; }
                                          else if (changes.host_changed) { badgeCls = 'badge-warning'; badgeLabel = 'Устарел'; }
                                          else if (changes.links_changed) { badgeCls = 'badge-neutral'; badgeLabel = 'Локации'; }
                                          return (
                                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px', borderRadius: '6px', backgroundColor: 'var(--bg-tertiary)' }}>
                                              <span style={{ fontSize: '12px', color: 'var(--text-tertiary)', whiteSpace: 'nowrap', minWidth: '130px' }}>
                                                {item.detected_at ? new Date(item.detected_at).toLocaleString() : '-'}
                                              </span>
                                              <span className={`badge ${badgeCls}`}>{badgeLabel}</span>
                                              <span style={{ fontSize: '12px', color: 'var(--text-secondary)', flex: 1 }}>
                                                {(changes.changes_summary || []).join('; ')}
                                              </span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                >
                  Назад
                </button>
                <span style={{ padding: '0 16px' }}>
                  Страница {page} из {totalPages}
                </span>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page === totalPages}
                >
                  Вперёд
                </button>
              </div>
            )}
          </div>
        </div>
        ) : (
          /* All Links View */
          <div>
            <div className="card">
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
              }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: 'var(--text-primary)',
                  margin: 0,
                }}>
                  Все ссылки ({allLinksTotal} всего, показано {getPaginatedLinks().length} на странице {allLinksPage} из {Math.ceil(allLinksTotal / allLinksLimit)})
                </h3>
                <button
                  onClick={openBulkTagModal}
                  disabled={allLinksTotal === 0}
                  style={{
                    padding: '8px 16px',
                    border: 'none',
                    borderRadius: '6px',
                    backgroundColor: allLinksTotal === 0 ? '#9ca3af' : '#10b981',
                    color: 'white',
                    cursor: allLinksTotal === 0 ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    opacity: allLinksTotal === 0 ? 0.5 : 1,
                  }}
                >
                  + Добавить теги отфильтрованным хостам
                </button>
              </div>

              {allLinksLoading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div style={{ color: 'var(--text-secondary)' }}>Загрузка ссылок...</div>
                </div>
              ) : (
                <>
                  <div style={{ overflowX: 'auto' }}>
                    <table className="table" style={{ tableLayout: 'fixed', width: '100%' }}>
                  <thead>
                    <tr>
                      <th style={{ cursor: 'pointer', width: linksColumnWidths.url, position: 'relative' }} onClick={() => handleAllLinksSort('url')}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          URL ссылки
                          {allLinksSortBy === 'url' && (
                            allLinksSortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                          )}
                        </div>
                        <div
                          onMouseDown={(e) => handleResizeStart('links', 'url', e)}
                          style={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            bottom: 0,
                            width: '4px',
                            cursor: 'col-resize',
                            backgroundColor: resizingColumn?.table === 'links' && resizingColumn?.columnKey === 'url' ? '#3b82f6' : 'transparent',
                            zIndex: 1
                          }}
                        />
                      </th>
                      <th style={{ cursor: 'pointer', width: linksColumnWidths.host, position: 'relative' }} onClick={() => handleAllLinksSort('host')}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          Хост
                          {allLinksSortBy === 'host' && (
                            allLinksSortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                          )}
                        </div>
                        <div
                          onMouseDown={(e) => handleResizeStart('links', 'host', e)}
                          style={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            bottom: 0,
                            width: '4px',
                            cursor: 'col-resize',
                            backgroundColor: resizingColumn?.table === 'links' && resizingColumn?.columnKey === 'host' ? '#3b82f6' : 'transparent',
                            zIndex: 1
                          }}
                        />
                      </th>
                      <th style={{ cursor: 'pointer', width: linksColumnWidths.status, position: 'relative' }} onClick={() => handleAllLinksSort('status')}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          Статус
                          {allLinksSortBy === 'status' && (
                            allLinksSortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                          )}
                        </div>
                        <div
                          onMouseDown={(e) => handleResizeStart('links', 'status', e)}
                          style={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            bottom: 0,
                            width: '4px',
                            cursor: 'col-resize',
                            backgroundColor: resizingColumn?.table === 'links' && resizingColumn?.columnKey === 'status' ? '#3b82f6' : 'transparent',
                            zIndex: 1
                          }}
                        />
                      </th>
                      <th style={{ cursor: 'pointer', width: linksColumnWidths.size, position: 'relative' }} onClick={() => handleAllLinksSort('size')}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          Размер
                          {allLinksSortBy === 'size' && (
                            allLinksSortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                          )}
                        </div>
                        <div
                          onMouseDown={(e) => handleResizeStart('links', 'size', e)}
                          style={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            bottom: 0,
                            width: '4px',
                            cursor: 'col-resize',
                            backgroundColor: resizingColumn?.table === 'links' && resizingColumn?.columnKey === 'size' ? '#3b82f6' : 'transparent',
                            zIndex: 1
                          }}
                        />
                      </th>
                      <th style={{ cursor: 'pointer', width: linksColumnWidths.tech, position: 'relative' }} onClick={() => handleAllLinksSort('tech')}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          Технологии
                          {allLinksSortBy === 'tech' && (
                            allLinksSortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                          )}
                        </div>
                        <div
                          onMouseDown={(e) => handleResizeStart('links', 'tech', e)}
                          style={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            bottom: 0,
                            width: '4px',
                            cursor: 'col-resize',
                            backgroundColor: resizingColumn?.table === 'links' && resizingColumn?.columnKey === 'tech' ? '#3b82f6' : 'transparent',
                            zIndex: 1
                          }}
                        />
                      </th>
                      <th style={{ cursor: 'pointer', width: linksColumnWidths.ip, position: 'relative' }} onClick={() => handleAllLinksSort('ip')}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          IP-адрес
                          {allLinksSortBy === 'ip' && (
                            allLinksSortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                          )}
                        </div>
                        <div
                          onMouseDown={(e) => handleResizeStart('links', 'ip', e)}
                          style={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            bottom: 0,
                            width: '4px',
                            cursor: 'col-resize',
                            backgroundColor: resizingColumn?.table === 'links' && resizingColumn?.columnKey === 'ip' ? '#3b82f6' : 'transparent',
                            zIndex: 1
                          }}
                        />
                      </th>
                      <th style={{ width: linksColumnWidths.actions }}>Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getPaginatedLinks().map((link) => (
                      <React.Fragment key={link.id}>
                        <tr>
                          <td style={{ width: linksColumnWidths.url, overflow: 'hidden' }}>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                            }}>
                              <LinkIcon size={16} color="#6b7280" />
                              <span style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}>
                                {link.url || 'Н/Д'}
                              </span>
                            </div>
                          </td>
                          <td style={{ width: linksColumnWidths.host, overflow: 'hidden' }}>
                            <span style={{
                              backgroundColor: '#eff6ff',
                              color: '#1d4ed8',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '12px',
                            }}>
                              {link.hostUrl || 'Н/Д'}
                            </span>
                          </td>
                          <td style={{ width: linksColumnWidths.status, overflow: 'hidden' }}>
                            {link.status_code ? (
                              <span style={{
                                backgroundColor: `${getStatusColor(link.status_code)}20`,
                                color: getStatusColor(link.status_code),
                                padding: '2px 8px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: '500',
                              }}>
                                {link.status_code}
                              </span>
                            ) : (
                              <span style={{ color: '#6b7280', fontSize: '12px' }}>Н/Д</span>
                            )}
                          </td>
                          <td style={{ width: linksColumnWidths.size, overflow: 'hidden' }}>
                            {link.size ? (
                              <span style={{
                                backgroundColor: '#f0fdf4',
                                color: '#16a34a',
                                padding: '2px 8px',
                                borderRadius: '12px',
                                fontSize: '12px',
                              }}>
                                {link.size} байт
                              </span>
                            ) : (
                              <span style={{ color: '#6b7280', fontSize: '12px' }}>Н/Д</span>
                            )}
                          </td>
                          <td style={{ width: linksColumnWidths.tech, overflow: 'hidden' }}>
                            {link.tech && link.tech.length > 0 ? (
                              <span style={{
                                backgroundColor: '#fef3c7',
                                color: '#92400e',
                                padding: '2px 8px',
                                borderRadius: '12px',
                                fontSize: '12px',
                              }}>
                                {link.tech.length} тех.
                              </span>
                            ) : (
                              <span style={{ color: '#6b7280', fontSize: '12px' }}>Н/Д</span>
                            )}
                          </td>
                          <td style={{ width: linksColumnWidths.ip, overflow: 'hidden' }}>
                            {link.ip ? (
                              <span style={{
                                backgroundColor: '#fef2f2',
                                color: '#991b1b',
                                padding: '2px 8px',
                                borderRadius: '12px',
                                fontSize: '12px',
                              }}>
                                {link.ip}
                              </span>
                            ) : (
                              <span style={{ color: '#6b7280', fontSize: '12px' }}>Н/Д</span>
                            )}
                          </td>
                          <td style={{ width: linksColumnWidths.actions, overflow: 'hidden' }}>
                            <button
                              onClick={() => toggleLinkExpansion(link.id)}
                              className="btn btn-primary"
                              style={{ padding: '4px 8px', fontSize: '12px' }}
                            >
                              {expandedLinks.has(link.id) ? (
                                <>
                                  <EyeOff size={12} style={{ marginRight: '4px' }} />
                                  Скрыть
                                </>
                              ) : (
                                <>
                                  <Eye size={12} style={{ marginRight: '4px' }} />
                                  Показать
                                </>
                              )}
                            </button>
                          </td>
                        </tr>
                        
                        {/* Expanded Link Details */}
                        {expandedLinks.has(link.id) && (
                          <tr>
                            <td colSpan="7" style={{ padding: '0' }}>
                              <div style={{
                                backgroundColor: 'var(--bg-tertiary)',
                                padding: '16px',
                                borderTop: '1px solid var(--border-color)',
                              }}>
                                <div style={{ marginBottom: '16px' }}>
                                  <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    marginBottom: '8px',
                                  }}>
                                    <ExternalLink size={16} color="#3b82f6" />
                                    <strong>URL ссылки:</strong>
                                  </div>
                                  <div style={{
                                    backgroundColor: 'var(--bg-secondary)',
                                    color: 'var(--text-primary)',
                                    padding: '8px',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    wordBreak: 'break-all',
                                    border: '1px solid var(--border-color)',
                                  }}>
                                    {link.url}
                                  </div>
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                  <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    marginBottom: '8px',
                                  }}>
                                    <Server size={16} color="#f59e0b" />
                                    <strong>Хост:</strong>
                                  </div>
                                  <div style={{
                                    backgroundColor: 'var(--bg-secondary)',
                                    color: 'var(--text-primary)',
                                    padding: '8px',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    wordBreak: 'break-all',
                                    border: '1px solid var(--border-color)',
                                  }}>
                                    {link.hostUrl || 'Н/Д'}
                                  </div>
                                </div>

                                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                                  {link.status_code && (
                                    <div style={{ flex: '1', minWidth: '150px' }}>
                                      <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        marginBottom: '8px',
                                      }}>
                                        <strong>Статус-код:</strong>
                                      </div>
                                      <div style={{
                                        backgroundColor: `${getStatusColor(link.status_code)}20`,
                                        color: getStatusColor(link.status_code),
                                        padding: '8px',
                                        borderRadius: '6px',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        border: `1px solid ${getStatusColor(link.status_code)}40`,
                                      }}>
                                        {link.status_code}
                                      </div>
                                    </div>
                                  )}

                                  {link.size && (
                                    <div style={{ flex: '1', minWidth: '150px' }}>
                                      <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        marginBottom: '8px',
                                      }}>
                                        <strong>Размер:</strong>
                                      </div>
                                      <div style={{
                                        backgroundColor: '#f0fdf4',
                                        color: '#16a34a',
                                        padding: '8px',
                                        borderRadius: '6px',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        border: '1px solid #bbf7d0',
                                      }}>
                                        {link.size} байт
                                      </div>
                                    </div>
                                  )}
                                </div>

                                {link.tech && link.tech.length > 0 && (
                                  <div style={{ marginTop: '16px' }}>
                                    <div style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '8px',
                                      marginBottom: '8px',
                                    }}>
                                      <Cpu size={16} color="#f59e0b" />
                                      <strong>Технологии ({link.tech.length}):</strong>
                                    </div>
                                    <div style={{
                                      display: 'flex',
                                      flexWrap: 'wrap',
                                      gap: '6px',
                                    }}>
                                      {link.tech.map((tech, index) => (
                                        <span
                                          key={index}
                                          style={{
                                            backgroundColor: '#fef3c7',
                                            color: '#92400e',
                                            padding: '4px 8px',
                                            borderRadius: '12px',
                                            fontSize: '12px',
                                          }}
                                        >
                                          {tech}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                    </tbody>
                  </table>
                  </div>
                  {Math.ceil(allLinksTotal / allLinksLimit) > 1 && (
                    <div className="pagination">
                      <button
                        onClick={() => setAllLinksPage(allLinksPage - 1)}
                        disabled={allLinksPage === 1}
                      >
                        Назад
                      </button>
                      <span style={{ padding: '0 16px' }}>
                        Страница {allLinksPage} из {Math.ceil(allLinksTotal / allLinksLimit)}
                      </span>
                      <button
                        onClick={() => setAllLinksPage(allLinksPage + 1)}
                        disabled={allLinksPage === Math.ceil(allLinksTotal / allLinksLimit)}
                      >
                        Вперёд
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Bulk Tag Modal */}
      {showBulkTagModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: 'var(--bg-secondary)',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
          }}>
            <h2 style={{
              fontSize: '20px',
              fontWeight: 'bold',
              marginBottom: '16px',
              color: 'var(--text-primary)',
            }}>
              Добавить теги отфильтрованным хостам
            </h2>

            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                marginBottom: '8px',
                color: 'var(--text-primary)',
              }}>
                Теги (через запятую):
              </label>
              <input
                type="text"
                value={bulkTagInput}
                onChange={(e) => setBulkTagInput(e.target.value)}
                placeholder="например: production, critical, bitrix"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)',
                }}
                autoFocus
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <div style={{
                fontSize: '14px',
                fontWeight: '500',
                marginBottom: '8px',
                color: 'var(--text-primary)',
              }}>
                Хосты для тегирования ({bulkTagPreview.length}):
              </div>
              <div style={{
                maxHeight: '200px',
                overflowY: 'auto',
                backgroundColor: 'var(--bg-tertiary)',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                padding: '12px',
              }}>
                {bulkTagPreview.length > 0 ? (
                  <ul style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0,
                  }}>
                    {bulkTagPreview.map((host, idx) => (
                      <li
                        key={idx}
                        style={{
                          padding: '4px 0',
                          fontSize: '12px',
                          color: 'var(--text-secondary)',
                          borderBottom: idx < bulkTagPreview.length - 1 ? '1px solid var(--border-color)' : 'none',
                        }}
                      >
                        {host}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>
                    Нет хостов для тегирования
                  </div>
                )}
              </div>
            </div>

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
            }}>
              <button
                onClick={closeBulkTagModal}
                style={{
                  padding: '10px 20px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                Отмена
              </button>
              <button
                onClick={applyBulkTags}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                }}
              >
                Применить теги
              </button>
            </div>
          </div>
        </div>
      )}

      {showImportModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: 'var(--bg-secondary)',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '720px',
            width: '95%',
            maxHeight: '85vh',
            overflow: 'auto',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
          }}>
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '12px', color: 'var(--text-primary)' }}>
              Загрузка JSON хостов
            </h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '12px' }}>
              Загрузите JSON в формате `config.json`. Будут добавлены только новые хосты; существующие и дубли в файле будут пропущены.
            </p>

            <div style={{ marginBottom: '12px' }}>
              <input
                type="file"
                accept=".json,application/json"
                onChange={handleImportFileSelect}
                disabled={importLoading}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)',
                }}
              />
            </div>

            {importFileName && (
              <div style={{ marginBottom: '12px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                Файл: <strong>{importFileName}</strong>
              </div>
            )}

            {importSummary && (
              <div style={{
                marginBottom: '16px',
                backgroundColor: 'var(--bg-tertiary)',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                padding: '12px',
                fontSize: '13px',
                color: 'var(--text-primary)'
              }}>
                <div>Всего во входных данных: {importSummary.total_input ?? 0}</div>
                <div>Создано: {importSummary.created ?? 0}</div>
                <div>Пропущено (уже существуют): {importSummary.skipped_existing ?? 0}</div>
                <div>Пропущено дублей в файле: {importSummary.skipped_duplicates_in_payload ?? 0}</div>
                <div>Невалидных записей: {importSummary.invalid ?? 0}</div>
                {(importSummary.errors || []).length > 0 && (
                  <div style={{ marginTop: '8px', color: '#ef4444', fontSize: '12px' }}>
                    Первые ошибки: {(importSummary.errors || []).slice(0, 5).map(item => `#${item.index}: ${item.error}`).join(' | ')}
                  </div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button className="btn btn-secondary" onClick={closeImportModal} disabled={importLoading}>Отмена</button>
              <button
                className="btn btn-primary"
                onClick={applyImportHosts}
                disabled={importLoading || importHostsPayload.length === 0}
              >
                {importLoading ? 'Обработка...' : 'Применить импорт'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRegexDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: 'var(--bg-secondary)',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '680px',
            width: '95%',
            maxHeight: '85vh',
            overflow: 'auto',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
          }}>
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '12px', color: 'var(--text-primary)' }}>
              Удаление хостов по регулярному выражению
            </h2>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: '8px', marginBottom: '12px' }}>
              <input
                type="text"
                placeholder="Шаблон регулярного выражения для поля host"
                value={deleteRegexPattern}
                onChange={e => setDeleteRegexPattern(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)',
                }}
              />
              <input
                type="text"
                placeholder="флаги"
                value={deleteRegexFlags}
                onChange={e => setDeleteRegexFlags(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)',
                }}
              />
            </div>

            {deleteRegexSummary && (
              <div style={{
                marginBottom: '16px',
                backgroundColor: 'var(--bg-tertiary)',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                padding: '12px',
                fontSize: '13px',
                color: 'var(--text-primary)'
              }}>
                <div>Совпадений: {deleteRegexSummary.matched_count ?? 0}</div>
                {!!(deleteRegexSummary.preview_hosts || deleteRegexSummary.deleted_preview)?.length && (
                  <div style={{ marginTop: '8px', maxHeight: '150px', overflow: 'auto', fontSize: '12px', color: 'var(--text-secondary)' }}>
                    {(deleteRegexSummary.preview_hosts || deleteRegexSummary.deleted_preview || []).slice(0, 50).join(', ')}
                  </div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
              <button
                className="btn btn-secondary"
                onClick={() => runRegexDelete(true)}
                disabled={deleteRegexLoading}
              >
                {deleteRegexLoading ? 'Обработка...' : 'Пробный прогон'}
              </button>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button className="btn btn-secondary" onClick={closeRegexDeleteModal} disabled={deleteRegexLoading}>Отмена</button>
                <button
                  className="btn btn-danger"
                  onClick={() => runRegexDelete(false)}
                  disabled={deleteRegexLoading}
                >
                  {deleteRegexLoading ? 'Удаление...' : 'Удалить совпавшие'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditHostModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: 'var(--bg-secondary)',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '900px',
            width: '95%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
          }}>
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '12px', color: 'var(--text-primary)' }}>
              Редактирование JSON хоста
            </h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '10px' }}>
              ID хоста: {editingHostRef?._id}
            </p>
            <textarea
              value={editingHostJson}
              onChange={e => setEditingHostJson(e.target.value)}
              rows={22}
              style={{
                width: '100%',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                backgroundColor: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
                fontFamily: 'monospace',
                fontSize: '12px',
                padding: '10px',
                resize: 'vertical',
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '12px' }}>
              <button className="btn btn-secondary" onClick={closeEditHostModal} disabled={editHostLoading}>Отмена</button>
              <button className="btn btn-primary" onClick={saveEditedHost} disabled={editHostLoading}>
                {editHostLoading ? 'Сохранение...' : 'Сохранить хост'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Hosts;
